<template>
  <div class="grid-scan-details-container">
    <!-- Loading State with Skeleton -->
    <div v-if="isLoading" class="loading-container">
      <div class="skeleton-header">
        <div class="skeleton-box" style="height: 40px; width: 300px; margin-bottom: 10px;"></div>
        <div class="skeleton-box" style="height: 20px; width: 500px;"></div>
      </div>
      
      <div class="skeleton-stats">
        <div class="skeleton-card" v-for="i in 4" :key="i">
          <div class="skeleton-box" style="height: 60px; width: 100%; margin-bottom: 10px;"></div>
          <div class="skeleton-box" style="height: 20px; width: 80%;"></div>
        </div>
      </div>
      
      <div class="skeleton-section">
        <div class="skeleton-box" style="height: 30px; width: 200px; margin-bottom: 20px;"></div>
        <div class="skeleton-box" style="height: 400px; width: 100%;"></div>
      </div>
    </div>
    
    <!-- No Data State -->
    <div v-else-if="!isLoading && !hasData" class="no-data-container">
      <div class="no-data-card">
        <div class="no-data-icon">📊</div>
        <h2>Grid Scan Not Yet Executed</h2>
        <p>This grid scan is scheduled but hasn't run yet. The first execution will happen automatically based on your schedule settings.</p>
        
        <div class="schedule-info">
          <div class="schedule-item">
            <span class="schedule-label">Scan Frequency:</span>
            <span class="schedule-value">{{ scanData.scanFrequency || 'Not set' }}</span>
          </div>
          <div class="schedule-item">
            <span class="schedule-label">Scan Time:</span>
            <span class="schedule-value">{{ scanData.scanTime || 'Not set' }}</span>
          </div>
          <div class="schedule-item next-run">
            <span class="schedule-label">Next Scheduled Run:</span>
            <span class="schedule-value">{{ formatNextRun(scanData.nextRunAt) }}</span>
          </div>
        </div>
        
        <!-- Keywords Management Section -->
        <!-- <div class="keywords-management" v-if="scanData.keywords">
          <h3 style="margin-top: 24px; margin-bottom: 16px; font-size: 18px; color: #111827;">Keywords to be tracked:</h3>
          <div class="keywords-list" style="margin-bottom: 20px;">
            <span v-for="(kw, idx) in scanData.keywords" :key="idx" class="keyword-tag" style="margin: 4px;">
              {{ kw }}
            </span>
          </div>
          <button class="add-keyword-btn" @click="openAddKeywordModal" style="margin-bottom: 20px;">
            + Add Keyword
          </button>
        </div> -->
        
        <div class="no-data-actions">
          <button class="action-btn primary" @click="goBack">
            ← Back to Dashboard
          </button>
        </div>
      </div>
    </div>
    
    <!-- Main Content (shown when data exists) -->
    <div v-else>
    <!-- Header with Key Metrics -->
    <div class="header">
      <div class="header-top">
        <div class="header-info">
          <h1>🎯 Local GRID Rank Tracker</h1>
          <p>Comprehensive insights into your local search performance with competitor analysis</p>
        </div>
        <div class="header-actions">
          <button class="action-btn" @click="openAddKeywordModal">
            <span class="btn-icon">➕</span>
            Add Keyword
          </button>
          <button class="action-btn" @click="showCompetitorsModal = true">
            <span class="btn-icon">👥</span>
            Competitors
          </button>
          <button class="action-btn" @click="showExportModal = true">
            <span class="btn-icon">📊</span>
            Export
          </button>
          <!-- <button class="action-btn" @click="showScheduleModal = true">
            <span class="btn-icon">📅</span>
            Schedule
          </button> -->
          <button class="action-btn" @click="showShareModal = true">
            <span class="btn-icon">🔗</span>
            Share
          </button>
        </div>
      </div>
      
      <div class="header-stats">
        <div class="stat-card keywords-card" @click="focusOnMetric('keywords')">
          <div class="stat-number">{{ stats.totalKeywords || 0 }}</div>
          <div class="stat-label">Keywords Tracked</div>
          
          <div class="keyword-breakdown">
            <div class="breakdown-item">
              <div class="breakdown-label">
                <div class="breakdown-dot segment-1-3"></div>
                Positions 1-3
              </div>
              <div class="breakdown-count">{{ (stats.positions && stats.positions.top3) || 0 }}</div>
            </div>
            
            <div class="breakdown-item">
              <div class="breakdown-label">
                <div class="breakdown-dot segment-4-7"></div>
                Positions 4-7
              </div>
              <div class="breakdown-count">{{ (stats.positions && stats.positions.pos4to7) || 0 }}</div>
            </div>
            
            <div class="breakdown-item">
              <div class="breakdown-label">
                <div class="breakdown-dot segment-8-15"></div>
                Positions 8-15
              </div>
              <div class="breakdown-count">{{ (stats.positions && stats.positions.pos8to15) || 0 }}</div>
            </div>
            
            <div class="breakdown-item">
              <div class="breakdown-label">
                <div class="breakdown-dot segment-16-plus"></div>
                Positions 16-20
              </div>
              <div class="breakdown-count">{{ (stats.positions && stats.positions.pos16to20) || 0 }}</div>
            </div>
            
            <div class="breakdown-item">
              <div class="breakdown-label">
                <div class="breakdown-dot" style="background: #9ca3af;"></div>
                Not Ranked
              </div>
              <div class="breakdown-count" style="background: #f3f4f6; color: #6b7280;">{{ (stats.positions && stats.positions.notRanked) || 0 }}</div>
            </div>
            
            
          </div>
          
          <div class="breakdown-visual" v-if="stats.positions && stats.totalProcessItems > 0">
            <div class="breakdown-segment segment-1-3" 
                 :style="{ width: getPositionPercentage('top3') + '%' }"></div>
            <div class="breakdown-segment segment-4-7" 
                 :style="{ width: getPositionPercentage('pos4to7') + '%' }"></div>
            <div class="breakdown-segment segment-8-15" 
                 :style="{ width: getPositionPercentage('pos8to15') + '%' }"></div>
            <div class="breakdown-segment segment-16-plus" 
                 :style="{ width: getPositionPercentage('pos16to20') + '%' }"></div>
            <div class="breakdown-segment segment-not-ranked" 
                 :style="{ width: getPositionPercentage('notRanked') + '%' }"></div>
          </div>
        </div>
        
        <div class="stat-card" @click="focusOnMetric('locations')">
          <div class="stat-number">{{ stats.totalLocations || 0 }}</div>
          <div class="stat-label">Locations Monitored</div>
          
          <div class="keyword-breakdown">
            <div class="breakdown-item">
              <div class="breakdown-label">
                <div class="breakdown-dot" style="background: #10b981;"></div>
                High Performance Areas
              </div>
              <div class="breakdown-count" style="background: #dcfce7; color: #16a34a;">{{ (stats.locationPerformance && stats.locationPerformance.high) || 0 }}</div>
            </div>
            
            <div class="breakdown-item">
              <div class="breakdown-label">
                <div class="breakdown-dot" style="background: #3b82f6;"></div>
                Good Performance Areas
              </div>
              <div class="breakdown-count" style="background: #dbeafe; color: #2563eb;">{{ (stats.locationPerformance && stats.locationPerformance.good) || 0 }}</div>
            </div>
            
            <div class="breakdown-item">
              <div class="breakdown-label">
                <div class="breakdown-dot" style="background: #f59e0b;"></div>
                Improvement Needed
              </div>
              <div class="breakdown-count" style="background: #fef3c7; color: #d97706;">{{ (stats.locationPerformance && stats.locationPerformance.improvement) || 0 }}</div>
            </div>
            
            <div class="breakdown-item">
              <div class="breakdown-label">
                <div class="breakdown-dot" style="background: #ef4444;"></div>
                Priority Focus Areas
              </div>
              <div class="breakdown-count" style="background: #fecaca; color: #dc2626;">{{ (stats.locationPerformance && stats.locationPerformance.priority) || 0 }}</div>
            </div>
          </div>
          
          <div class="breakdown-visual" style="margin-top: 12px;" v-if="stats.totalLocations > 0">
            <div class="breakdown-segment" 
                 style="background: #10b981;" 
                 :style="{ width: ((stats.locationPerformance && stats.locationPerformance.high || 0) / stats.totalLocations * 100) + '%' }">
            </div>
            <div class="breakdown-segment" 
                 style="background: #3b82f6;" 
                 :style="{ width: ((stats.locationPerformance && stats.locationPerformance.good || 0) / stats.totalLocations * 100) + '%' }">
            </div>
            <div class="breakdown-segment" 
                 style="background: #f59e0b;" 
                 :style="{ width: ((stats.locationPerformance && stats.locationPerformance.improvement || 0) / stats.totalLocations * 100) + '%' }">
            </div>
            <div class="breakdown-segment" 
                 style="background: #ef4444;" 
                 :style="{ width: ((stats.locationPerformance && stats.locationPerformance.priority || 0) / stats.totalLocations * 100) + '%' }">
            </div>
          </div>
        </div>
        
        <div class="stat-card" @click="focusOnMetric('position')">
          <div class="stat-number">{{ stats.avgPosition || '0' }}</div>
          <div class="stat-label">Average Position</div>
          
          <div class="keyword-breakdown">
            <div class="breakdown-item">
              <div class="breakdown-label">
                <div class="breakdown-dot" style="background: #10b981;"></div>
                Best Performing Keywords
              </div>
              <div class="breakdown-count" style="background: #dcfce7; color: #16a34a;">{{ (stats.averagePositionBreakdown && stats.averagePositionBreakdown.bestPerforming) || '0' }}</div>
            </div>
            
            <div class="breakdown-item">
              <div class="breakdown-label">
                <div class="breakdown-dot" style="background: #4f46e5;"></div>
                This Month's Average
              </div>
              <div class="breakdown-count" style="background: #dbeafe; color: #2563eb;">{{ (stats.averagePositionBreakdown && stats.averagePositionBreakdown.thisMonth) || '0' }}</div>
            </div>
            
            <div class="breakdown-item">
              <div class="breakdown-label">
                <div class="breakdown-dot" style="background: #FFB443;"></div>
                Last Month's Average
              </div>
              <div class="breakdown-count" style="background: #fef3c7; color: #d97706;">{{ (stats.averagePositionBreakdown && stats.averagePositionBreakdown.lastMonth) || '0' }}</div>
            </div>
            
            <div class="breakdown-item">
              <div class="breakdown-label">
                <div class="breakdown-dot" style="background: #8b5cf6;"></div>
                Target Position Goal
              </div>
              <div class="breakdown-count" style="background: #ede9fe; color: #7c3aed;">{{ (stats.averagePositionBreakdown && stats.averagePositionBreakdown.targetGoal) || '5.0' }}</div>
            </div>
          </div>
          
          <div class="breakdown-visual" style="margin-top: 12px;">
            <div class="breakdown-segment" style="background: #10b981; width: 25%;"></div>
            <div class="breakdown-segment" style="background: #4f46e5; width: 35%;"></div>
            <div class="breakdown-segment" style="background: #FFB443; width: 25%;"></div>
            <div class="breakdown-segment" style="background: #8b5cf6; width: 15%;"></div>
          </div>
        </div>
        
        <div class="stat-card" @click="focusOnMetric('visibility')">
          <div class="stat-number">{{ (stats.visibility && stats.visibility.score) || '0' }}%</div>
          <div class="stat-label">Visibility Score</div>
          
          <div class="keyword-breakdown">
            <div class="breakdown-item">
              <div class="breakdown-label">
                <div class="breakdown-dot" style="background: #10b981;"></div>
                Local Pack Appearances
              </div>
              <div class="breakdown-count" style="background: #dcfce7; color: #16a34a;">{{ (stats.visibility && stats.visibility.localPackAppearances) || '0' }}%</div>
            </div>
          
            
            <div class="breakdown-item">
              <div class="breakdown-label">
                <div class="breakdown-dot" style="background: #FFB443;"></div>
                Monthly Growth Rate
              </div>
              <div class="breakdown-count" style="background: #fef3c7; color: #d97706;">
                {{ stats.visibility && stats.visibility.monthlyGrowth ? 
                   (stats.visibility.monthlyGrowth > 0 ? '+' : '') + stats.visibility.monthlyGrowth + '%' : 
                   'N/A' }}
              </div>
            </div>
            
            <div class="breakdown-item">
              <div class="breakdown-label">
                <div class="breakdown-dot" style="background: #8b5cf6;"></div>
                Competitor Advantage
              </div>
              <div class="breakdown-count" style="background: #ede9fe; color: #7c3aed;">
                {{ stats.visibility && stats.visibility.competitorAdvantage ? 
                   (stats.visibility.competitorAdvantage > 0 ? '+' : '') + stats.visibility.competitorAdvantage + '%' : 
                   'N/A' }}
              </div>
            </div>
          </div>
          
          <div class="breakdown-visual" style="margin-top: 12px;">
            <div class="breakdown-segment" style="background: #10b981; width: 28%;"></div>
            <div class="breakdown-segment" style="background: #4f46e5; width: 32%;"></div>
            <div class="breakdown-segment" style="background: #FFB443; width: 25%;"></div>
            <div class="breakdown-segment" style="background: #8b5cf6; width: 15%;"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Geographic Performance Section -->
    <div class="section geographic-performance-section">
      <div style="margin-bottom: 32px;">
        <div class="geographic-header" style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
          <h3 style="font-family: 'Rubik', sans-serif; font-weight: 600; font-size: 1.125rem; color: #19233A; margin: 0;">
            🗺️ Average Geographic Performance Overview
          </h3>
        </div>
        
        <div class="legend" style="margin-bottom: 20px;">
          <div class="legend-item">
            <div class="legend-color rank-1-3"></div>
            <span>Positions 1-3</span>
          </div>
          <div class="legend-item">
            <div class="legend-color rank-4-7"></div>
            <span>Positions 4-7</span>
          </div>
          <div class="legend-item">
            <div class="legend-color rank-8-15"></div>
            <span>Positions 8-15</span>
          </div>
          <div class="legend-item">
            <div class="legend-color rank-16-plus"></div>
            <span>Position 16+</span>
          </div>
          <div class="legend-item">
            <div class="legend-color not-ranked"></div>
            <span>Not Ranked</span>
          </div>
        </div>

        <!-- Google Maps Container with Side Panel -->
        <div style="position: relative;">
          <div class="google-maps-container" style="position: relative; height: 500px; border-radius: 16px; overflow: hidden; border: 1px solid #E5E7EB;">
            <div id="googleMap" style="width: 100%; height: 100%;"></div>
            
            <!-- Custom Map Controls -->
            <div class="map-controls" style="position: absolute; top: 16px; right: 16px; display: flex; flex-direction: column; gap: 8px; z-index: 10;">
              <button class="map-control-btn" @click="zoomInMap" title="Zoom In">+</button>
              <button class="map-control-btn" @click="zoomOutMap" title="Zoom Out">−</button>
              <button class="map-control-btn" @click="resetMapView" title="Reset View">⌂</button>
            </div>
            
            <!-- Grid Info Panel -->
            <div class="grid-info-panel" style="position: absolute; bottom: 16px; left: 16px; background: rgba(255, 255, 255, 0.95); padding: 12px 16px; border-radius: 8px; backdrop-filter: blur(8px); box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15); z-index: 10;">
              <div style="font-size: 0.875rem; font-weight: 600; color: #19233A; margin-bottom: 4px;">
                Grid: {{ scanData.gridSize || '5×5' }} ({{ gridOverlays.length }} locations)
              </div>
              <div style="font-size: 0.75rem; color: #757B89;">
                Radius: {{ scanData.radius || '2' }}{{ scanData.distanceType === 'km' ? 'km' : 'mi' }} • Keywords: {{ scanData.keywords ? scanData.keywords.length : 0 }}
              </div>
            </div>
            
            <!-- Location Details Panel -->
            <transition name="slide-fade">
              <div v-if="showLocationPanel" class="location-panel" style="position: absolute; top: 10px; right: 16px; width: 400px; height: 90%; background: white; box-shadow: -4px 0 12px rgba(0, 0, 0, 0.15); z-index: 20; overflow-y: auto; border-radius: 16px;">
                <div class="location-panel-header" style="padding: 16px; background: #19233A; color: white;">
                  <div style="display: flex; justify-content: space-between; align-items: center;">
                    <div style="font-weight: 600; font-size: 14px;">Searched results for this location:</div>
                    <button @click="closeLocationPanel" style="background: none; border: none; color: white; font-size: 24px; cursor: pointer; padding: 0; width: 24px; height: 24px; display: flex; align-items: center; justify-content: center;">×</button>
                  </div>
                  <div style="margin-top: 8px;">
                    <span style="background: rgba(255, 255, 255, 0.15); padding: 4px 12px; border-radius: 12px; font-size: 12px; display: inline-block;">
                      📍 {{ selectedLocationCoords }}
                    </span>
                  </div>
                </div>
                <div class="location-panel-content" style="padding: 16px;">
                  <div v-if="selectedLocationData">
                    <!-- Average Rank Display -->
                    <div style="margin-bottom: 20px; padding: 16px; background: #f9fafb; border-radius: 8px;">
                      <div style="display: flex; align-items: center; justify-content: space-between;">
                        <div>
                          <div style="font-size: 12px; color: #6b7280; margin-bottom: 4px;">Average Rank</div>
                          <div style="font-size: 28px; font-weight: 700; color: #19233A;">#{{ selectedLocationData.avgRankDisplay }}</div>
                        </div>
                        <div style="text-align: right;">
                          <div style="font-size: 12px; color: #6b7280;">Keywords</div>
                          <div style="font-size: 20px; font-weight: 600; color: #4f46e5;">{{ selectedLocationData.keywords.length }}</div>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Keywords List -->
                    <div style="margin-bottom: 20px;">
                      <div style="font-weight: 600; font-size: 14px; color: #19233A; margin-bottom: 12px;">Keyword Rankings</div>
                      <div v-for="(keyword, index) in selectedLocationData.keywords" :key="index" 
                           style="display: flex; align-items: center; padding: 12px; margin-bottom: 8px; background: white; border: 1px solid #e5e7eb; border-radius: 8px;">
                        <div :style="`min-width: 32px; height: 32px; background: ${getRankColor(keyword.effectiveRank)}; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 14px; margin-right: 12px;`">
                          {{ index + 1 }}
                        </div>
                        <div style="flex: 1;">
                          <div style="font-weight: 600; font-size: 14px; color: #111827; margin-bottom: 2px;">{{ keyword.keyword }}</div>
                          <div style="font-size: 12px; color: #6b7280;">
                            Rank: <span :style="`font-weight: 600; color: ${getRankColor(keyword.effectiveRank)};`">#{{ keyword.rank === 0 ? '20+' : keyword.rank }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Competitors Section -->
                    <div v-if="selectedLocationData.competitors && selectedLocationData.competitors.length > 0">
                      <div style="font-weight: 600; font-size: 14px; color: #19233A; margin-bottom: 12px;">Top Competitors</div>
                      <div v-for="(comp, index) in selectedLocationData.competitors" :key="index"
                           style="display: flex; align-items: center; padding: 10px; margin-bottom: 6px; background: #fff5f5; border-radius: 8px;">
                        <div style="min-width: 24px; height: 24px; background: #dc2626; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 600; font-size: 11px; margin-right: 10px;">
                          {{ index + 1 }}
                        </div>
                        <div style="flex: 1;">
                          <div style="font-size: 13px; font-weight: 500; color: #111827;">{{ comp.name }}</div>
                          <div style="font-size: 11px; color: #6b7280;">Average Rank: #{{ comp.avgDisplay }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>

    <!-- Competitive Position Analysis -->
    <div class="section" v-if="hasCompetitors">
      <div class="section-header">
        <h2 class="section-title">🗺️ Competitive Position Analysis</h2>
        <div class="filter-controls" v-if="!executionId">
          <select class="filter-select" v-model="competitorTimeframe">
            <option value="current">Current Rankings</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>
        </div>
      </div>
      
      <!-- Timeframe Info Banner - Above all content -->
      <transition name="fade-slide">
        <div v-if="competitorTimeframe !== 'current' && competitorAnalysisData && competitorAnalysisData.summary && !isLoadingCompetitorAnalysis" 
             style="background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%); 
                    border: 1px solid #bae6fd; 
                    border-radius: 12px; 
                    padding: 16px 20px; 
                    margin-top: 16px;
                    margin-bottom: 20px;
                    display: flex;
                    align-items: center;
                    gap: 16px;">
          <div style="background: #0ea5e9; 
                      color: white; 
                      width: 40px; 
                      height: 40px; 
                      border-radius: 10px; 
                      display: flex; 
                      align-items: center; 
                      justify-content: center;
                      font-size: 20px;
                      flex-shrink: 0;">
            {{ competitorTimeframe === 'week' ? '📅' : '📊' }}
          </div>
          <div style="flex: 1;">
            <div style="font-weight: 600; 
                        color: #0c4a6e; 
                        font-size: 0.9375rem; 
                        margin-bottom: 4px;">
              {{ competitorTimeframe === 'week' ? 'Weekly Performance Analysis' : 'Monthly Performance Analysis' }}
            </div>
            <div style="color: #0369a1; 
                        font-size: 0.8125rem; 
                        line-height: 1.4;">
              Showing aggregated data from 
              <span style="font-weight: 600;">{{ competitorAnalysisData.summary.executionCount }}</span> 
              scan{{ competitorAnalysisData.summary.executionCount !== 1 ? 's' : '' }}
              {{ competitorTimeframe === 'week' ? 'over the past 7 days' : 'over the past 30 days' }}.
              Rankings represent cumulative performance across all executions.
            </div>
          </div>
          <div style="background: white; 
                      border: 1px solid #e0f2fe; 
                      border-radius: 8px; 
                      padding: 8px 16px;
                      min-width: 120px;
                      text-align: center;">
            <div style="font-size: 1.25rem; 
                        font-weight: 700; 
                        color: #0c4a6e;">
              {{ competitorAnalysisData.summary.executionCount }}
            </div>
            <div style="font-size: 0.6875rem; 
                        color: #64748b; 
                        text-transform: uppercase; 
                        letter-spacing: 0.5px;">
              Total Scans
            </div>
          </div>
        </div>
      </transition>
      
      <!-- Loading state with skeleton loader -->
      <v-skeleton-loader 
        v-if="isLoadingCompetitorAnalysis"
        type="article"
        class="competitive-skeleton"
      ></v-skeleton-loader>

      <!-- Actual content -->
      <div v-else class="competitive-grid">
        <div class="competitor-rankings-widget">
          <h3>📊 Position Distribution by Competitor</h3>
          <div class="rankings-table">
            <table style="width: 100%; border-collapse: collapse;">
              <thead>
                <tr style="background: #f9fafb; border-bottom: 2px solid #e5e7eb;">
                  <th style="padding: 12px; text-align: left; font-weight: 600; color: #374151; font-size: 0.875rem;">Business</th>
                  <th style="padding: 12px; text-align: center; font-weight: 600; color: #374151; font-size: 0.875rem;">Top 3</th>
                  <th style="padding: 12px; text-align: center; font-weight: 600; color: #374151; font-size: 0.875rem;">4-7</th>
                  <th style="padding: 12px; text-align: center; font-weight: 600; color: #374151; font-size: 0.875rem;">8-15</th>
                  <th style="padding: 12px; text-align: center; font-weight: 600; color: #374151; font-size: 0.875rem;">16-20</th>
                  <th style="padding: 12px; text-align: center; font-weight: 600; color: #374151; font-size: 0.875rem;">Not Ranked</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(business, index) in competitorRankingDistribution" :key="index" 
                    :style="{ 
                      background: business.isYourBusiness ? 'rgba(79, 70, 229, 0.05)' : (index % 2 === 0 ? '#ffffff' : '#f9fafb'),
                      borderBottom: '1px solid #e5e7eb'
                    }">
                  <td style="padding: 12px; font-weight: 500; color: #111827; font-size: 0.875rem;">
                    <div style="display: flex; align-items: center; gap: 8px;">
                      <span v-if="business.isYourBusiness" style="color: #4f46e5; font-weight: 600;">⭐</span>
                      {{ business.name }}
                    </div>
                  </td>
                  <td style="padding: 12px; text-align: center; font-size: 0.875rem;">
                    <span :style="{ 
                      padding: '4px 8px', 
                      borderRadius: '6px', 
                      background: business.top3 > 0 ? '#dcfce7' : '#f3f4f6',
                      color: business.top3 > 0 ? '#166534' : '#9ca3af',
                      fontWeight: 600
                    }">
                      {{ business.top3 }}
                    </span>
                  </td>
                  <td style="padding: 12px; text-align: center; font-size: 0.875rem;">
                    <span :style="{ 
                      padding: '4px 8px', 
                      borderRadius: '6px', 
                      background: business.pos4to7 > 0 ? '#ddd6fe' : '#f3f4f6',
                      color: business.pos4to7 > 0 ? '#4c1d95' : '#9ca3af',
                      fontWeight: 600
                    }">
                      {{ business.pos4to7 }}
                    </span>
                  </td>
                  <td style="padding: 12px; text-align: center; font-size: 0.875rem;">
                    <span :style="{ 
                      padding: '4px 8px', 
                      borderRadius: '6px', 
                      background: business.pos8to15 > 0 ? '#fed7aa' : '#f3f4f6',
                      color: business.pos8to15 > 0 ? '#92400e' : '#9ca3af',
                      fontWeight: 600
                    }">
                      {{ business.pos8to15 }}
                    </span>
                  </td>
                  <td style="padding: 12px; text-align: center; font-size: 0.875rem;">
                    <span :style="{ 
                      padding: '4px 8px', 
                      borderRadius: '6px', 
                      background: business.pos16to20 > 0 ? '#fee2e2' : '#f3f4f6',
                      color: business.pos16to20 > 0 ? '#991b1b' : '#9ca3af',
                      fontWeight: 600
                    }">
                      {{ business.pos16to20 }}
                    </span>
                  </td>
                  <td style="padding: 12px; text-align: center; font-size: 0.875rem;">
                    <span :style="{ 
                      padding: '4px 8px', 
                      borderRadius: '6px', 
                      background: business.notRanked > 0 ? '#f3f4f6' : '#f3f4f6',
                      color: business.notRanked > 0 ? '#374151' : '#9ca3af',
                      fontWeight: 600
                    }">
                      {{ business.notRanked }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="seo-leader-widget">
          <h3>👑 Current Local SEO Leader</h3>
          <div class="leader-card">
            <div :style="{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '20px',
              background: seoLeaderData.isYourBusiness ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)',
              borderRadius: '12px',
              color: '#FFFFFF',
              position: 'relative',
              overflow: 'hidden'
            }">
              <div :style="{ zIndex: 1 }">
                <div :style="{ fontSize: '0.75rem', opacity: 0.9, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }">
                  {{ seoLeaderData.isYourBusiness ? '🎉 CONGRATULATIONS!' : '🎯 CURRENT LEADER' }}
                </div>
                <div :style="{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '8px' }">
                  {{ seoLeaderData.name }}
                </div>
                <div :style="{ fontSize: '0.875rem', opacity: 0.9 }">
                  Dominating <span :style="{ fontWeight: 600 }">{{ seoLeaderData.keywordsCount }} keywords</span> with avg position #{{ seoLeaderData.avgPosition }}
                </div>
              </div>
              <div :style="{ textAlign: 'center', background: 'rgba(255, 255, 255, 0.2)', padding: '16px', borderRadius: '12px', minWidth: '100px' }">
                <div :style="{ fontSize: '2.5rem', fontWeight: 700, lineHeight: 1 }">{{ seoLeaderData.topPositions }}</div>
                <div :style="{ fontSize: '0.75rem', marginTop: '4px', opacity: 0.9 }">Top 3 Positions</div>
              </div>
              <div :style="{
                position: 'absolute',
                top: '-50%',
                right: '-50px',
                width: '200px',
                height: '200px',
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '50%'
              }"></div>
            </div>
            <div :style="{ fontSize: '0.875rem', color: '#757B89', marginBottom: '16px', marginTop: '16px' }">
              {{ seoLeaderData.marketShare }}% market share • {{ seoLeaderData.topPositions }} top positions • Avg rank #{{ seoLeaderData.avgPosition }}
            </div>
          </div>
          
          <div class="leader-stats">
            <div :style="{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginTop: '16px' }">
              <div :style="{ background: '#f9fafb', padding: '16px', borderRadius: '10px', border: '1px solid #E5E7EB' }">
                <div :style="{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }">
                  <span :style="{ fontSize: '0.8125rem', color: '#757B89', fontWeight: 500 }">Market Share</span>
                  <span :style="{ fontSize: '1.25rem', fontWeight: 700, color: '#19233A' }">{{ seoLeaderData.marketShare }}%</span>
                </div>
                <div :style="{ background: '#E5E7EB', height: '4px', borderRadius: '2px', overflow: 'hidden' }">
                  <div :style="{ background: '#10b981', height: '100%', width: seoLeaderData.marketShare + '%', transition: 'width 0.5s ease' }"></div>
                </div>
              </div>
              
              <div :style="{ background: '#f9fafb', padding: '16px', borderRadius: '10px', border: '1px solid #E5E7EB' }">
                <div :style="{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }">
                  <span :style="{ fontSize: '0.8125rem', color: '#757B89', fontWeight: 500 }">Lead Margin</span>
                  <span :style="{ fontSize: '1.25rem', fontWeight: 700, color: '#19233A' }">+{{ seoLeaderData.leadMargin }}</span>
                </div>
                <div :style="{ fontSize: '0.75rem', color: '#757B89' }">top 3 positions ahead</div>
              </div>
              
              <div :style="{ background: '#f9fafb', padding: '16px', borderRadius: '10px', border: '1px solid #E5E7EB' }">
                <div :style="{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }">
                  <span :style="{ fontSize: '0.8125rem', color: '#757B89', fontWeight: 500 }">Momentum</span>
                  <div :style="{ display: 'flex', alignItems: 'center', gap: '4px' }">
                    <span :style="{ fontSize: '1.25rem' }">{{ seoLeaderData.momentum.icon }}</span>
                    <span :style="{ fontSize: '1.125rem', fontWeight: 700, color: seoLeaderData.momentum.color }">{{ seoLeaderData.momentum.text }}</span>
                  </div>
                </div>
                <div :style="{ fontSize: '0.75rem', color: '#757B89' }">{{ seoLeaderData.momentum.description }}</div>
              </div>
              
              <div :style="{ background: '#f9fafb', padding: '16px', borderRadius: '10px', border: '1px solid #E5E7EB' }">
                <div :style="{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }">
                  <span :style="{ fontSize: '0.8125rem', color: '#757B89', fontWeight: 500 }">First Positions</span>
                  <span :style="{ fontSize: '1.25rem', fontWeight: 700, color: '#19233A' }">{{ seoLeaderData.firstPositions }}</span>
                </div>
                <div :style="{ fontSize: '0.75rem', color: '#757B89' }">out of {{ seoLeaderData.keywordsCount }} keywords</div>
              </div>
            </div>
            
            <div v-if="!seoLeaderData.isYourBusiness" :style="{ 
              marginTop: '16px', 
              padding: '16px', 
              background: '#FEF3C7', 
              borderRadius: '10px', 
              border: '1px solid #FDE68A' 
            }">
              <div :style="{ display: 'flex', alignItems: 'center', gap: '12px' }">
                <span :style="{ fontSize: '1.5rem' }">📊</span>
                <div>
                  <div :style="{ fontSize: '0.875rem', fontWeight: 600, color: '#92400E', marginBottom: '4px' }">
                    Gap Analysis
                  </div>
                  <div :style="{ fontSize: '0.8125rem', color: '#B45309' }">
                    You need <strong>{{ seoLeaderData.gapToClose }} more top positions</strong> to become the leader
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="performance-comparison-widget">
          <h3>📈 Average Position Comparison</h3>
          <div class="comparison-chart-container">
            <canvas id="competitorComparisonChart" width="400" height="300"></canvas>
          </div>
        </div>
      </div>
    </div>
    
    <!-- No Competitors Message -->
    <div class="section" v-if="!hasCompetitors">
      <div style="background: #f9fafb; border: 2px dashed #e5e7eb; border-radius: 12px; padding: 48px; text-align: center;">
        <div style="font-size: 3rem; margin-bottom: 16px;">🎯</div>
        <h3 style="font-size: 1.25rem; font-weight: 600; color: #111827; margin-bottom: 8px;">No Competitors Added</h3>
        <p style="font-size: 0.875rem; color: #6b7280; max-width: 400px; margin: 0 auto;">
          Add competitors to track and compare your performance against other businesses in your area.
        </p>
        <button @click="showAddCompetitorModal" style="margin-top: 24px; padding: 10px 20px; background: #4f46e5; color: white; border: none; border-radius: 8px; font-weight: 500; cursor: pointer; font-size: 0.875rem;">
          + Add Competitors
        </button>
      </div>
    </div>

    <!-- Keywords Section -->
    <div class="section">
      <div class="section-header">
        <h2 class="section-title">🔍 Keyword Performance & Competitor Analysis</h2>
      </div>

      <div class="keyword-grid">
        <div 
          v-for="(keyword, index) in dynamicKeywordData" 
          :key="index"
          :class="['keyword-card', { 'expanded': expandedKeyword === index }]"
          @click="toggleKeywordDetails(index)"
        >
          <div class="keyword-header">
            <div class="keyword-name">{{ keyword.name }}</div>
            <div class="keyword-rank">
              <span class="rank-position">#{{ keyword.position }}</span>
              <span :class="['rank-change', getRankChangeClass(keyword.trend, keyword.change)]">
                {{ keyword.change }}
              </span>
            </div>
          </div>
          
          <div class="keyword-stats">
            <div class="stat-item">
              <div class="stat-value">{{ keyword.avgPosition }}</div>
              <div class="stat-label-small">Avg Position</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">{{ keyword.bestPosition }}</div>
              <div class="stat-label-small">Best Position</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">{{ keyword.gridCoverage }}%</div>
              <div class="stat-label-small">Grid Coverage</div>
            </div>
          </div>
          
          <div class="keyword-details">
            <!-- Loading overlay -->
            <div v-if="keywordLoadingStates && keywordLoadingStates[index]" 
                 style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; 
                        background: rgba(255, 255, 255, 0.9); z-index: 100; 
                        display: flex; align-items: center; justify-content: center;
                        border-radius: 12px;">
              <div style="text-align: center;">
                <div style="font-size: 2rem; margin-bottom: 8px;">⏳</div>
                <div style="color: #4f46e5; font-weight: 600;">Updating data...</div>
              </div>
            </div>
            
            <div class="keyword-period-controls">
              <h4>Detailed Analysis</h4>
              <div class="keyword-controls-row">
                <select v-if="!executionId" class="keyword-period-select" @click.stop @change="updateKeywordData(index, $event.target.value)">
                  <option value="7">Last 7 Days</option>
                  <option value="30" selected>Last 30 Days</option>
                  <option value="90">Last 90 Days</option>
                  <option value="180">Last 6 Months</option>
                </select>
                <button 
                  class="keyword-export-btn"
                  @click.stop="openKeywordExportModal(index, keyword)"
                  title="Export keyword data"
                >
                  <span class="btn-icon">📊</span>
                  Export
                </button>
              </div>
            </div>
            
            <div class="keyword-tabs">
              <button 
                :class="['keyword-tab', { 'active': getActiveTab(index) === 'charts' }]"
                @click.stop="switchKeywordTab(index, 'charts')"
              >
                📊 Charts
              </button>
              <button 
                :class="['keyword-tab', { 'active': getActiveTab(index) === 'heatmap' }]"
                @click.stop="switchKeywordTab(index, 'heatmap')"
              >
                🗺️ Heatmap
              </button>
              <button 
                :class="['keyword-tab', { 'active': getActiveTab(index) === 'competitors' }]"
                @click.stop="switchKeywordTab(index, 'competitors')"
              >
                🏆 Competitors
              </button>
            </div>
            
            <!-- Charts Tab -->
            <div class="keyword-tab-content" :style="{ display: getActiveTab(index) === 'charts' ? 'block' : 'none' }">
              <div class="keyword-charts-grid">
                <div class="keyword-chart-container">
                  <canvas :id="'keyword-rank-chart-' + index"></canvas>
                </div>
                <div class="keyword-chart-container">
                  <canvas :id="'keyword-grid-chart-' + index"></canvas>
                </div>
              </div>
            </div>
            
            <!-- Heatmap Tab -->
            <div class="keyword-tab-content" :style="{ display: getActiveTab(index) === 'heatmap' ? 'block' : 'none' }">
              <div class="keyword-heatmap-legend">
                <div class="legend-item">
                  <div class="legend-color rank-1-3"></div>
                  <span>Positions 1-3</span>
                </div>
                <div class="legend-item">
                  <div class="legend-color rank-4-7"></div>
                  <span>Positions 4-7</span>
                </div>
                <div class="legend-item">
                  <div class="legend-color rank-8-15"></div>
                  <span>Positions 8-15</span>
                </div>
                <div class="legend-item">
                  <div class="legend-color rank-16-plus"></div>
                  <span>Position 16+</span>
                </div>
                <div class="legend-item">
                  <div class="legend-color not-ranked"></div>
                  <span>Not Ranked</span>
                </div>
              </div>
              <div class="keyword-heatmap-container">
                <div class="keyword-map-wrapper">
                  <div :ref="'keywordMap' + index" class="keyword-heatmap"></div>
                  <div class="keyword-map-controls">
                    <button class="keyword-map-control-btn" @click.stop="zoomInKeywordMap(index)" title="Zoom In">+</button>
                    <button class="keyword-map-control-btn" @click.stop="zoomOutKeywordMap(index)" title="Zoom Out">−</button>
                    <button class="keyword-map-control-btn" @click.stop="resetKeywordMapView(index)" title="Reset">⌂</button>
                  </div>
                  <div class="keyword-grid-info">
                    <div class="keyword-grid-info-title">"{{ keyword.name }}"</div>
                    <div class="keyword-grid-info-details">Grid: 5×5 • Avg: #{{ keyword.avgPosition }}</div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Competitors Tab -->
            <div class="keyword-tab-content" :style="{ display: getActiveTab(index) === 'competitors' ? 'block' : 'none' }">
              <div class="competitors-tab-content">
                <div class="competitors-section">
                  <h5>Current Rankings for "{{ keyword.name }}"</h5>
                  <div>
                    <div 
                      v-for="(comp, compIndex) in (keywordDetailedData[index] && keywordDetailedData[index].competitors ? keywordDetailedData[index].competitors : keyword.competitors)" 
                      :key="compIndex"
                      class="competitor-item"
                      :style="{ background: comp.name === 'Your Business' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255, 255, 255, 0.1)' }"
                    >
                      <div class="competitor-info">
                        <span class="competitor-name">{{ comp.name }}</span>
                        <span class="competitor-duration">{{ comp.duration }}</span>
                      </div>
                      <div class="competitor-rank">
                        <span class="competitor-position">#{{ comp.position }}</span>
                        <span :class="['rank-change', getRankChangeClass(null, comp.change)]">
                          {{ comp.change }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <button class="close-btn" @click.stop="closeKeywordDetails">Close Analysis</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Export Modal -->
    <div v-if="showExportModal" class="modal" @click="closeModal('export')">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">📊 Export Report</h3>
          <button class="close-modal" @click="closeModal('export')">&times;</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>Export Format:</label>
            <select v-model="exportFormat">
              <option value="pdf">PDF Report</option>
              <option value="csv">CSV Data</option>
              <!-- <option value="xlsx">Excel Spreadsheet</option> -->
              <!-- <option value="png">PNG Image</option> -->
            </select>
          </div>
          
          <div class="export-description">
            <p v-if="exportFormat === 'pdf'">
              Export a comprehensive PDF report with performance metrics, charts, and detailed grid analysis.
            </p>
            <p v-else-if="exportFormat === 'csv'">
              Export all grid scan data including rankings, coordinates, keywords, and location details to a CSV file.
            </p>
            <p v-else-if="exportFormat === 'png'">
              Export a PNG image of the geographic performance map with grid points visualization.
            </p>
          </div>
          <!-- <div class="form-group">
            <label>Date Range:</label>
            <select v-model="exportDateRange">
              <option value="7">Last 7 Days</option>
              <option value="30">Last 30 Days</option>
              <option value="90">Last 90 Days</option>
              <option value="180">Last 6 Months</option>
              <option value="365">Last Year</option>
            </select>
          </div> -->
          <!-- <div class="form-group">
            <label>Include:</label>
            <div style="display: flex; gap: 20px; margin-top: 10px;">
              <label style="display: flex; align-items: center; gap: 8px;">
                <input type="checkbox" v-model="includeHeatmap"> Heatmap
              </label>
              <label style="display: flex; align-items: center; gap: 8px;">
                <input type="checkbox" v-model="includeKeywordData"> Keyword Data
              </label>
              <label style="display: flex; align-items: center; gap: 8px;">
                <input type="checkbox" v-model="includeCompetitorAnalysis"> Competitor Analysis
              </label>
            </div>
          </div> -->
        </div>
        <div class="modal-footer">
          <button class="action-btn" @click="closeModal('export')">Cancel</button>
          <button class="action-btn primary" @click="processExport">Download Report</button>
        </div>
      </div>
    </div>

    <!-- Keyword Export Modal -->
    <div v-if="showKeywordExportModal" class="modal" @click="closeKeywordExportModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">📊 Export Keyword Report</h3>
          <button class="close-modal" @click="closeKeywordExportModal">&times;</button>
        </div>
        <div class="modal-body">
          <div class="export-keyword-info">
            <div class="export-info-item">
              <strong>Keyword:</strong> {{ selectedKeywordForExport ? selectedKeywordForExport.name : '' }}
            </div>
            <div class="export-info-item">
              <strong>Date Range:</strong> {{ getDateRangeText(selectedKeywordDateRange) }}
            </div>
          </div>
          
          <div class="form-group">
            <label>Export Format:</label>
            <select v-model="keywordExportFormat">
              <option value="pdf">PDF Report</option>
              <option value="csv">CSV Data</option>
            </select>
          </div>
          
          <div class="export-description">
            <p v-if="keywordExportFormat === 'pdf'">
              Export a comprehensive PDF report with all performance metrics, charts, and detailed analysis for "{{ selectedKeywordForExport ? selectedKeywordForExport.name : '' }}".
            </p>
            <p v-else-if="keywordExportFormat === 'csv'">
              Export complete data including rankings, grid positions, and competitor information for "{{ selectedKeywordForExport ? selectedKeywordForExport.name : '' }}" in CSV format.
            </p>
          </div>
        </div>
        <div class="modal-footer">
          <button class="action-btn" @click="closeKeywordExportModal">Cancel</button>
          <button class="action-btn primary" @click="processKeywordExport">Download Report</button>
        </div>
      </div>
    </div>

    <!-- Schedule Modal -->
    <div v-if="showScheduleModal" class="modal" @click="closeModal('schedule')">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">📅 Schedule Reports</h3>
          <button class="close-modal" @click="closeModal('schedule')">&times;</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>Email Recipients:</label>
            <textarea v-model="emailRecipients" placeholder="Enter email addresses separated by commas" rows="3"></textarea>
          </div>
          <div class="form-group">
            <label>Frequency:</label>
            <select v-model="reportFrequency">
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
            </select>
          </div>
          <div class="form-group">
            <label>Report Format:</label>
            <select v-model="scheduleFormat">
              <option value="pdf">PDF Report</option>
              <option value="csv">CSV Data</option>
              <option value="both">Both PDF & CSV</option>
            </select>
          </div>
          <div class="form-group">
            <label>Report Name:</label>
            <input type="text" v-model="reportName" placeholder="Weekly Ranking Report">
          </div>
        </div>
        <div class="modal-footer">
          <button class="action-btn" @click="closeModal('schedule')">Cancel</button>
          <button class="action-btn primary" @click="setupSchedule">Schedule Reports</button>
        </div>
      </div>
    </div>

    <!-- Share Modal -->
    <div v-if="showShareModal" class="modal" @click="closeModal('share')">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">🔗 Share Report</h3>
          <button class="close-modal" @click="closeModal('share')">&times;</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>Share Type:</label>
            <select v-model="shareType" @change="updateShareOptions">
              <option value="live">Live Dashboard Link</option>
              <option value="snapshot">Snapshot Report</option>
              <option value="white-label">White-label Report</option>
            </select>
          </div>
          <div class="form-group">
            <label>Access Level:</label>
            <select v-model="accessLevel">
              <option value="view">View Only</option>
              <option value="comment">View & Comment</option>
              <option value="limited">Limited Data View</option>
            </select>
          </div>
          <div class="form-group">
            <label>Expiration:</label>
            <select v-model="linkExpiration">
              <option value="never">Never</option>
              <option value="24h">24 Hours</option>
              <option value="7d">7 Days</option>
              <option value="30d">30 Days</option>
              <option value="90d">90 Days</option>
            </select>
          </div>
          <div class="share-url-container">
            <label>Shareable URL:</label>
            <div class="share-url" style="background: #f9fafb; padding: 12px; border-radius: 8px; border: 1px solid #E5E7EB; font-family: monospace; font-size: 0.875rem; color: #374151; word-break: break-all; margin: 8px 0;">
              {{ shareUrl }}
            </div>
            <button class="action-btn" @click="copyShareLink" style="width: 100%; margin-top: 10px;">
              📋 Copy Link
            </button>
          </div>
        </div>
        <div class="modal-footer">
          <button class="action-btn" @click="closeModal('share')">Close</button>
          <button class="action-btn" @click="generateNewShareLink">Generate New Link</button>
        </div>
      </div>
    </div>

    <!-- Competitors Modal -->
    <div v-if="showCompetitorsModal" class="modal" @click="closeModal('competitors')">
      <div class="modal-content" @click.stop style="max-width: 800px;">
        <div class="modal-header">
          <h3 class="modal-title">👥 Manage Competitors</h3>
          <button class="close-modal" @click="closeModal('competitors')">&times;</button>
        </div>
        <div class="modal-body">
          <!-- Info message -->
          <div style="padding: 12px; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 6px; margin-bottom: 20px;">
            <div style="display: flex; align-items: start; gap: 8px;">
              <span style="font-size: 1.2rem;">ℹ️</span>
              <div style="font-size: 14px; color: #1e40af;">
                <div style="font-weight: 500; margin-bottom: 4px;">No re-scan needed!</div>
                <div>Adding or removing competitors only affects analytics. Your existing scan data will be re-analyzed with the updated competitor list.</div>
              </div>
            </div>
          </div>
          
          <!-- Add Competitor Section -->
          <div class="form-group" style="margin-bottom: 30px;">
            <label style="font-weight: 600; margin-bottom: 12px; display: block;">Add New Competitor:</label>
            <div style="position: relative;">
              <input 
                ref="competitorAutocompleteInput"
                type="text" 
                placeholder="Search for business name (e.g., Pizza Hut, McDonald's)..."
                style="width: 100%; padding: 12px; border: 1px solid #e5e7eb; border-radius: 6px; font-size: 14px;"
              />
              <div style="font-size: 12px; color: #6b7280; margin-top: 8px;">
                Start typing to search for businesses. Select from the dropdown to add as competitor.
              </div>
            </div>
          </div>
          
          <!-- Current Competitors List -->
          <div class="form-group">
            <label style="font-weight: 600; margin-bottom: 12px; display: block;">Current Competitors ({{ currentCompetitors.length }}):</label>
            <div class="competitor-list" style="max-height: 400px; overflow-y: auto;">
              <div 
                v-for="(competitor, index) in currentCompetitors" 
                :key="competitor.id || index" 
                class="modal-competitor-item"
                style="display: flex; align-items: center; justify-content: space-between; padding: 12px; margin-bottom: 8px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px;"
              >
                <div style="flex: 1;">
                  <div class="competitor-name" style="font-weight: 500; color: #111827;">{{ competitor.name }}</div>
                  <div v-if="competitor.business_data && competitor.business_data.address" style="font-size: 12px; color: #6b7280; margin-top: 2px;">
                    {{ competitor.business_data.address }}
                  </div>
                </div>
                <button 
                  class="action-btn danger small" 
                  @click="removeCompetitor(competitor.id)"
                  :disabled="isRemovingCompetitor"
                  style="padding: 4px 12px; font-size: 12px;"
                >
                  {{ isRemovingCompetitor ? 'Removing...' : 'Remove' }}
                </button>
              </div>
              <div v-if="currentCompetitors.length === 0" class="empty-competitors" style="text-align: center; padding: 40px; color: #6b7280;">
                <div style="font-size: 2rem; margin-bottom: 8px;">🎯</div>
                <div>No competitors being tracked.</div>
                <div style="font-size: 14px; margin-top: 4px;">Search and add competitors above to benchmark your performance.</div>
              </div>
            </div>
          </div>
          
          <div v-if="competitorChangeMessage" 
               :style="{ 
                 padding: '12px', 
                 marginTop: '15px', 
                 background: competitorChangeMessage.type === 'success' ? '#f0fdf4' : '#fef2f2', 
                 border: competitorChangeMessage.type === 'success' ? '1px solid #bbf7d0' : '1px solid #fecaca', 
                 borderRadius: '6px',
                 color: competitorChangeMessage.type === 'success' ? '#065f46' : '#991b1b'
               }">
            {{ competitorChangeMessage.text }}
          </div>
        </div>
        <div class="modal-footer" style="display: flex; justify-content: flex-end; gap: 10px; padding: 20px; border-top: 1px solid #e5e7eb;">
          <button class="action-btn" @click="closeModal('competitors')" style="padding: 10px 20px;">Close</button>
        </div>
      </div>
    </div>
    </div><!-- End of main content v-else -->

    <!-- Add Keyword Modal -->
    <div v-if="showAddKeywordModal" class="modal-overlay" @click.self="closeAddKeywordModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>Add New Keyword</h3>
          <button class="modal-close" @click="closeAddKeywordModal">×</button>
        </div>
        
        <div class="modal-body">
          <!-- Current Keywords Display -->
          <div class="current-keywords-section">
            <label>Current Keywords:</label>
            <div class="keywords-list">
              <span v-for="(kw, idx) in currentKeywords" :key="idx" class="keyword-tag">
                {{ kw }}
              </span>
            </div>
          </div>
          
          <!-- New Keyword Input -->
          <div class="form-group">
            <label for="newKeyword">New Keyword:</label>
            <input 
              id="newKeyword"
              v-model="newKeyword" 
              type="text" 
              placeholder="Enter new keyword"
              class="form-input"
              @keyup.enter="handleAddKeyword"
            />
          </div>
          
          <!-- Schedule Options (only for scheduled scans) -->
          <div v-if="isScheduledScan" class="schedule-options">
            <label>When to run the scan:</label>
            <div class="radio-group">
              <label class="radio-label">
                <input 
                  type="radio" 
                  v-model="runImmediately" 
                  :value="true"
                />
                <span>Run immediately for this keyword</span>
              </label>
              <label class="radio-label">
                <input 
                  type="radio" 
                  v-model="runImmediately" 
                  :value="false"
                />
                <span>Wait for next scheduled scan ({{ formatNextRun(scanData.nextRunAt) }})</span>
              </label>
            </div>
          </div>
          
          <!-- Info Message -->
          <div class="info-message" v-if="!isScheduledScan">
            <i class="info-icon">ℹ️</i>
            <span>This is a manual scan. The new keyword will be scanned immediately.</span>
          </div>
        </div>
        
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeAddKeywordModal">Cancel</button>
          <button 
            class="btn btn-primary" 
            @click="handleAddKeyword"
            :disabled="!newKeyword.trim() || isAddingKeyword"
          >
            {{ isAddingKeyword ? 'Adding...' : 'Add Keyword' }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
export default {
  metaInfo () {
    return {
      title: "Grid Scan | " + this.$appName
    }
  },
  name: 'GridScanDetails',
  data() {
    return {
      gridId: null,
      executionId: null, // Store execution ID for specific execution data
      isLoading: true,
      hasData: false,
      scanData: {},
      processItems: [],
      stats: {},
      competitors: [], // Store competitors from API
      competitorTimeframe: 'current',
      competitorAnalysisData: null, // Store fetched competitor analysis data
      isLoadingCompetitorAnalysis: false, // Loading state for competitor analysis
      sortBy: 'position',
      map: null,
      gridOverlays: [],
      gridRectangle: null,
      infoWindow: null,
      expandedKeyword: null,
      keywordMaps: {},
      keywordActiveTabs: {}, // Track active tab for each keyword
      keywordCharts: {}, // Store chart instances
      keywordOverlays: {}, // Store map overlays
      keywordDetailedData: {}, // Store fetched keyword data from backend
      keywordSelectedPeriods: {}, // Store selected periods for each keyword
      keywordLoadingStates: {}, // Store loading state for each keyword
      keywordInfoWindow: null, // Store info window for keyword map markers
      competitorChart: null, // Store competitor comparison chart instance
      showLocationPanel: false, // Show/hide location side panel
      selectedLocationData: null, // Data for selected location
      selectedLocationCoords: '', // Coordinates string for display
      // Add Keyword Modal
      showAddKeywordModal: false,
      newKeyword: '',
      currentKeywords: [],
      runImmediately: true,
      isAddingKeyword: false,
      // Modal states
      showExportModal: false,
      showScheduleModal: false,
      showShareModal: false,
      showCompetitorsModal: false,
      showKeywordExportModal: false, // New modal for keyword export
      competitorChangeMessage: null,
      isRemovingCompetitor: false,
      competitorAutocomplete: null,
      // Modal form data
      exportFormat: 'pdf',
      // Keyword export specific data
      keywordExportFormat: 'pdf',
      selectedKeywordForExport: null,
      selectedKeywordIndex: null,
      selectedKeywordDateRange: '30',
      includeKeywordCharts: true,
      includeKeywordHeatmap: true,
      includeKeywordCompetitors: true,
      includeKeywordTrends: true,
      exportDateRange: '30',
      includeHeatmap: true,
      includeKeywordData: true,
      includeCompetitorAnalysis: true,
      emailRecipients: '',
      reportFrequency: 'weekly',
      scheduleFormat: 'pdf',
      reportName: 'Weekly Ranking Report',
      shareType: 'live',
      accessLevel: 'view',
      linkExpiration: 'never',
      shareUrl: 'https://gridrank.com/share/abc123def456?access=view&expires=never',
      currentCompetitors: [],
      // SEO Leader data
      seoLeader: {
        name: "Tony's Pizza Palace",
        isYourBusiness: false,
        topPositions: 23,
        keywordsCount: 87,
        avgPosition: "2.8",
        marketShare: 42,
        leadMargin: 7,
        momentum: {
          icon: "🚀",
          text: "Rising",
          color: "#10b981",
          description: "Gaining positions"
        },
        firstPositions: 9,
        gapToClose: 8
      },
      keywordData: [
        {
          name: "Best pizza delivery",
          position: 3,
          change: "+2",
          trend: "up",
          avgPosition: 4.2,
          bestPosition: 1,
          gridCoverage: 73,
          gridPoints: 48,
          rankedPoints: 35,
          improvingPoints: 12,
          decliningPoints: 5,
          competitors: [
            { name: "Tony's Pizza Palace", position: 1, change: "+1", duration: "2 months at #1" },
            { name: "Mama Mia Restaurant", position: 2, change: "0", duration: "3 weeks at #2" },
            { name: "Your Business", position: 3, change: "+2", duration: "1 week at #3" },
            { name: "Giuseppe's Kitchen", position: 5, change: "-1", duration: "2 days at #5" }
          ]
        },
        {
          name: "Local Italian restaurant",
          position: 5,
          change: "+1",
          trend: "up",
          avgPosition: 6.8,
          bestPosition: 3,
          gridCoverage: 65,
          gridPoints: 48,
          rankedPoints: 31,
          improvingPoints: 8,
          decliningPoints: 7,
          competitors: [
            { name: "Mama Mia Restaurant", position: 2, change: "+1", duration: "5 days at #2" },
            { name: "Giuseppe's Kitchen", position: 4, change: "0", duration: "1 month at #4" },
            { name: "Your Business", position: 5, change: "+1", duration: "3 days at #5" },
            { name: "Tony's Pizza Palace", position: 7, change: "-2", duration: "1 day at #7" }
          ]
        },
        {
          name: "Pizza near me",
          position: 2,
          change: "0",
          trend: "stable",
          avgPosition: 3.1,
          bestPosition: 1,
          gridCoverage: 84,
          gridPoints: 48,
          rankedPoints: 40,
          improvingPoints: 6,
          decliningPoints: 3,
          competitors: [
            { name: "Giuseppe's Kitchen", position: 1, change: "0", duration: "2 weeks at #1" },
            { name: "Your Business", position: 2, change: "0", duration: "1 week at #2" },
            { name: "Tony's Pizza Palace", position: 3, change: "+1", duration: "2 days at #3" },
            { name: "Mama Mia Restaurant", position: 6, change: "-1", duration: "1 day at #6" }
          ]
        },
        {
          name: "Family restaurant",
          position: 8,
          change: "-1",
          trend: "down",
          avgPosition: 9.2,
          bestPosition: 6,
          gridCoverage: 45,
          gridPoints: 48,
          rankedPoints: 22,
          improvingPoints: 3,
          decliningPoints: 9,
          competitors: [
            { name: "Mama Mia Restaurant", position: 3, change: "+2", duration: "4 days at #3" },
            { name: "Giuseppe's Kitchen", position: 6, change: "0", duration: "1 week at #6" },
            { name: "Your Business", position: 8, change: "-1", duration: "2 days at #8" },
            { name: "Tony's Pizza Palace", position: 9, change: "-1", duration: "1 day at #9" }
          ]
        },
        {
          name: "Authentic Italian food",
          position: 12,
          change: "+3",
          trend: "up",
          avgPosition: 14.5,
          bestPosition: 8,
          gridCoverage: 32,
          gridPoints: 48,
          rankedPoints: 15,
          improvingPoints: 11,
          decliningPoints: 2,
          competitors: [
            { name: "Giuseppe's Kitchen", position: 4, change: "+1", duration: "3 days at #4" },
            { name: "Mama Mia Restaurant", position: 8, change: "0", duration: "2 weeks at #8" },
            { name: "Your Business", position: 12, change: "+3", duration: "1 day at #12" },
            { name: "Tony's Pizza Palace", position: 15, change: "-2", duration: "5 days at #15" }
          ]
        },
        {
          name: "Best restaurant downtown",
          position: 15,
          change: "-2",
          trend: "down",
          avgPosition: 16.7,
          bestPosition: 11,
          gridCoverage: 28,
          gridPoints: 48,
          rankedPoints: 13,
          improvingPoints: 2,
          decliningPoints: 8,
          competitors: [
            { name: "Giuseppe's Kitchen", position: 7, change: "+1", duration: "1 week at #7" },
            { name: "Tony's Pizza Palace", position: 11, change: "0", duration: "3 days at #11" },
            { name: "Your Business", position: 15, change: "-2", duration: "2 days at #15" },
            { name: "Mama Mia Restaurant", position: 18, change: "-3", duration: "1 day at #18" }
          ]
        }
      ],
      gridData: [
        // Row 1
        { rank: 2, competitors: [{ name: "Tony's Pizza Palace", position: 1 }, { name: "Your Business", position: 2 }, { name: "Giuseppe's Kitchen", position: 4 }] },
        { rank: 5, competitors: [{ name: "Mama Mia Restaurant", position: 2 }, { name: "Giuseppe's Kitchen", position: 3 }, { name: "Your Business", position: 5 }] },
        { rank: 12, competitors: [{ name: "Tony's Pizza Palace", position: 1 }, { name: "Roma Pizzeria", position: 9 }, { name: "Your Business", position: 12 }] },
        { rank: 18, competitors: [{ name: "Mama Mia Restaurant", position: 3 }, { name: "Giuseppe's Kitchen", position: 7 }, { name: "Your Business", position: 18 }] },
        { rank: 25, competitors: [{ name: "Tony's Pizza Palace", position: 2 }, { name: "Mama Mia Restaurant", position: 5 }, { name: "Your Business", position: 25 }] },
        // Row 2
        { rank: 1, competitors: [{ name: "Your Business", position: 1 }, { name: "Giuseppe's Kitchen", position: 3 }, { name: "Tony's Pizza Palace", position: 6 }] },
        { rank: 4, competitors: [{ name: "Giuseppe's Kitchen", position: 2 }, { name: "Your Business", position: 4 }, { name: "Roma Pizzeria", position: 8 }] },
        { rank: 8, competitors: [{ name: "Mama Mia Restaurant", position: 3 }, { name: "Your Business", position: 8 }, { name: "Luigi's Italian", position: 11 }] },
        { rank: 15, competitors: [{ name: "Tony's Pizza Palace", position: 4 }, { name: "Giuseppe's Kitchen", position: 9 }, { name: "Your Business", position: 15 }] },
        { rank: 22, competitors: [{ name: "Roma Pizzeria", position: 3 }, { name: "Luigi's Italian", position: 7 }, { name: "Your Business", position: 22 }] },
        // Row 3
        { rank: 3, competitors: [{ name: "Giuseppe's Kitchen", position: 1 }, { name: "Your Business", position: 3 }, { name: "Tony's Pizza Palace", position: 5 }] },
        { rank: 7, competitors: [{ name: "Mama Mia Restaurant", position: 2 }, { name: "Luigi's Italian", position: 4 }, { name: "Your Business", position: 7 }] },
        { rank: 6, competitors: [{ name: "Tony's Pizza Palace", position: 2 }, { name: "Your Business", position: 6 }, { name: "Roma Pizzeria", position: 9 }] },
        { rank: 14, competitors: [{ name: "Giuseppe's Kitchen", position: 5 }, { name: "Mama Mia Restaurant", position: 8 }, { name: "Your Business", position: 14 }] },
        { rank: 19, competitors: [{ name: "Luigi's Italian", position: 3 }, { name: "Roma Pizzeria", position: 6 }, { name: "Your Business", position: 19 }] },
        // Row 4
        { rank: 9, competitors: [{ name: "Tony's Pizza Palace", position: 2 }, { name: "Giuseppe's Kitchen", position: 5 }, { name: "Your Business", position: 9 }] },
        { rank: 11, competitors: [{ name: "Mama Mia Restaurant", position: 4 }, { name: "Your Business", position: 11 }, { name: "Luigi's Italian", position: 13 }] },
        { rank: 13, competitors: [{ name: "Giuseppe's Kitchen", position: 3 }, { name: "Roma Pizzeria", position: 7 }, { name: "Your Business", position: 13 }] },
        { rank: 16, competitors: [{ name: "Tony's Pizza Palace", position: 5 }, { name: "Mama Mia Restaurant", position: 9 }, { name: "Your Business", position: 16 }] },
        { rank: 21, competitors: [{ name: "Luigi's Italian", position: 4 }, { name: "Giuseppe's Kitchen", position: 8 }, { name: "Your Business", position: 21 }] },
        // Row 5
        { rank: 10, competitors: [{ name: "Roma Pizzeria", position: 2 }, { name: "Tony's Pizza Palace", position: 6 }, { name: "Your Business", position: 10 }] },
        { rank: 17, competitors: [{ name: "Giuseppe's Kitchen", position: 4 }, { name: "Mama Mia Restaurant", position: 11 }, { name: "Your Business", position: 17 }] },
        { rank: 20, competitors: [{ name: "Luigi's Italian", position: 5 }, { name: "Roma Pizzeria", position: 12 }, { name: "Your Business", position: 20 }] },
        { rank: 23, competitors: [{ name: "Tony's Pizza Palace", position: 7 }, { name: "Giuseppe's Kitchen", position: 15 }, { name: "Your Business", position: 23 }] },
        { rank: 24, competitors: [{ name: "Mama Mia Restaurant", position: 6 }, { name: "Luigi's Italian", position: 18 }, { name: "Your Business", position: 24 }] }
      ]
    }
  },
  watch: {
    competitorTimeframe(newValue, oldValue) {
      // Only fetch if not executionId mode and value actually changed
      if (!this.executionId && newValue !== oldValue) {
        this.fetchCompetitorAnalysis();
      }
    },
    
    showCompetitorsModal(newVal) {
      if (newVal) {
        // Ensure Google Maps is loaded before initializing autocomplete
        if (!window.google || !window.google.maps || !window.google.maps.places) {
          this.loadGoogleMaps(() => {
            this.$nextTick(() => {
              this.initCompetitorAutocomplete();
            });
          });
        } else {
          // Google Maps already loaded, initialize autocomplete
          this.$nextTick(() => {
            this.initCompetitorAutocomplete();
          });
        }
      }
    }
  },
  computed: {
    hasCompetitors() {
      return this.scanData.competitors && this.scanData.competitors.length > 0;
    },
    
    isScheduledScan() {
      return this.scanData.scanFrequency && this.scanData.scanFrequency !== 'manual';
    },
    
    timeframeDescription() {
      if (!this.competitorAnalysisData || !this.competitorAnalysisData.summary) {
        return '';
      }
      
      const summary = this.competitorAnalysisData.summary;
      if (this.competitorTimeframe === 'week') {
        return `Analyzing ${summary.executionCount} scan${summary.executionCount !== 1 ? 's' : ''} from the past week`;
      } else if (this.competitorTimeframe === 'month') {
        return `Analyzing ${summary.executionCount} scan${summary.executionCount !== 1 ? 's' : ''} from the past month`;
      }
      return '';
    },
    
    seoLeaderData() {
      // If we have fetched competitor analysis data, calculate from that
      if (this.competitorAnalysisData && this.competitorAnalysisData.competitors) {
        const competitorData = this.competitorAnalysisData.competitors;
        
        // Find the leader based on top positions
        let leader = null;
        let maxTopPositions = 0;
        
        competitorData.forEach(business => {
          const topPositions = business.top3 || 0;
          if (topPositions > maxTopPositions || (topPositions === maxTopPositions && business.isYourBusiness)) {
            maxTopPositions = topPositions;
            leader = business;
          }
        });
        
        if (leader) {
          // Calculate average position
          let totalRank = 0;
          let rankedCount = 0;
          
          totalRank += (leader.top3 || 0) * 2;
          rankedCount += (leader.top3 || 0);
          
          totalRank += (leader.pos4to7 || 0) * 5.5;
          rankedCount += (leader.pos4to7 || 0);
          
          totalRank += (leader.pos8to15 || 0) * 11.5;
          rankedCount += (leader.pos8to15 || 0);
          
          totalRank += (leader.pos16to20 || 0) * 18;
          rankedCount += (leader.pos16to20 || 0);
          
          const avgPosition = rankedCount > 0 ? Math.round(totalRank / rankedCount) : 21;
          const totalMeasurements = leader.totalMeasurements || (leader.top3 + leader.pos4to7 + leader.pos8to15 + leader.pos16to20 + leader.notRanked);
          const marketShare = totalMeasurements > 0 ? Math.round((leader.top3 / totalMeasurements) * 100) : 0;
          
          // Determine momentum based on market share
          let momentum = {
            icon: "➡️",
            text: "Stable",
            color: "#6b7280",
            description: "Maintaining position"
          };
          
          if (marketShare > 40) {
            momentum = {
              icon: "🚀",
              text: "Dominating",
              color: "#10b981",
              description: "Market leader"
            };
          } else if (marketShare > 25) {
            momentum = {
              icon: "📈",
              text: "Strong",
              color: "#3b82f6",
              description: "Above average"
            };
          }
          
          return {
            name: leader.name,
            isYourBusiness: leader.isYourBusiness,
            topPositions: leader.top3 || 0,
            avgPosition: avgPosition,
            marketShare: marketShare,
            keywordsCount: Math.ceil(totalMeasurements / (this.scanData.keywords ? this.scanData.keywords.length : 1)),
            momentum: momentum,
            firstPositions: leader.top3 || 0,
            leadMargin: 0,
            gapToClose: 0
          };
        }
        
        return this.getDefaultSeoLeader();
      }
      
      // Fall back to original logic using current execution data
      if (!this.scanData.execution || !this.scanData.execution.processItems) {
        return this.getDefaultSeoLeader();
      }
      
      const processItems = this.scanData.execution.processItems;
      const competitors = this.scanData.competitors || [];
      const businessName = this.scanData.businessName || 'Your Business';
      
      // Initialize performance tracking for all businesses
      const businessPerformance = {};
      
      // Initialize main business
      businessPerformance[businessName] = {
        name: businessName,
        isMainBusiness: true,
        totalAppearances: 0,
        totalRank: 0,
        rankedCount: 0,
        firstPositions: 0,
        topThreePositions: 0,
        top7Positions: 0,
        notRankedCount: 0,
        keywordSet: new Set()
      };
      
      // Initialize competitors
      competitors.forEach(comp => {
        businessPerformance[comp.place_id] = {
          name: comp.name,
          isMainBusiness: false,
          placeId: comp.place_id,
          totalAppearances: 0,
          totalRank: 0,
          rankedCount: 0,
          firstPositions: 0,
          topThreePositions: 0,
          top7Positions: 0,
          notRankedCount: 0,
          keywordSet: new Set()
        };
      });
      
      // Process each item to calculate performance metrics
      processItems.forEach(item => {
        const keyword = item.keyword || 'unknown';
        
        // Process main business
        const mainRank = parseInt(item.rank || 0);
        businessPerformance[businessName].keywordSet.add(keyword);
        
        if (mainRank > 0) {
          businessPerformance[businessName].totalRank += mainRank;
          businessPerformance[businessName].rankedCount++;
          businessPerformance[businessName].totalAppearances++;
          
          if (mainRank === 1) businessPerformance[businessName].firstPositions++;
          if (mainRank <= 3) businessPerformance[businessName].topThreePositions++;
          if (mainRank <= 7) businessPerformance[businessName].top7Positions++;
        } else {
          businessPerformance[businessName].notRankedCount++;
        }
        
        // Process competitors from result JSON
        if (item.result) {
          try {
            const results = JSON.parse(item.result);
            
            if (Array.isArray(results)) {
              results.forEach((business, index) => {
                const rank = index + 1;
                
                // Check each competitor
                competitors.forEach(comp => {
                  if ((business.place_id && business.place_id === comp.place_id) || 
                      (business.name && comp.name && 
                       business.name.toLowerCase().includes(comp.name.toLowerCase()))) {
                    
                    businessPerformance[comp.place_id].keywordSet.add(keyword);
                    businessPerformance[comp.place_id].totalAppearances++;
                    businessPerformance[comp.place_id].totalRank += rank;
                    businessPerformance[comp.place_id].rankedCount++;
                    
                    if (rank === 1) businessPerformance[comp.place_id].firstPositions++;
                    if (rank <= 3) businessPerformance[comp.place_id].topThreePositions++;
                    if (rank <= 7) businessPerformance[comp.place_id].top7Positions++;
                  }
                });
              });
            }
          } catch (e) {
            console.log('Error parsing result for SEO leader calculation:', e);
          }
        }
      });
      
      // Calculate average positions and scores for each business
      const scoredBusinesses = Object.values(businessPerformance)
        .filter(business => business.rankedCount > 0) // Only include businesses with rankings
        .map(business => {
          const avgPosition = (business.totalRank / business.rankedCount).toFixed(1);
          
          const totalPossibleAppearances = processItems.length;
          const marketShare = totalPossibleAppearances > 0 
            ? Math.round((business.topThreePositions / totalPossibleAppearances) * 100)
            : 0;
          
          // Calculate a performance score (lower is better)
          // Prioritize: 1st positions, then top 3, then average position
          const score = (business.firstPositions * -1000) + 
                       (business.topThreePositions * -100) + 
                       parseFloat(avgPosition);
          
          return {
            ...business,
            avgPosition,
            marketShare,
            score,
            keywordsCount: business.keywordSet.size
          };
        });
      
      // Sort by score (best first)
      scoredBusinesses.sort((a, b) => a.score - b.score);
      
      // Get the leader (best performer)
      const leader = scoredBusinesses[0];
      const secondBest = scoredBusinesses[1];
      
      if (!leader) {
        return this.getDefaultSeoLeader();
      }
      
      // Calculate lead margin (difference in top 3 positions count, not average position)
      const leadMargin = secondBest 
        ? Math.abs(leader.topThreePositions - secondBest.topThreePositions)
        : leader.topThreePositions;
      
      // Determine momentum based on current performance
      let momentum = {
        icon: "➡️",
        text: "Stable",
        color: "#6b7280",
        description: "Maintaining position"
      };
      
      if (leader.marketShare > 40) {
        momentum = {
          icon: "🚀",
          text: "Dominating",
          color: "#10b981",
          description: "Market leader"
        };
      } else if (leader.marketShare > 25) {
        momentum = {
          icon: "📈",
          text: "Strong",
          color: "#3b82f6",
          description: "Above average"
        };
      }
      
      // Calculate gap to close (for non-leaders)
      const mainBusinessData = businessPerformance[businessName];
      const mainBusinessTopPositions = mainBusinessData ? mainBusinessData.topThreePositions : 0;
      const gapToClose = leader.isMainBusiness 
        ? 0 
        : Math.max(0, leader.topThreePositions - mainBusinessTopPositions);
      
      return {
        name: leader.name,
        isYourBusiness: leader.isMainBusiness,
        topPositions: leader.topThreePositions,
        firstPositions: leader.firstPositions,
        keywordsCount: leader.keywordsCount,
        avgPosition: leader.avgPosition,
        marketShare: leader.marketShare,
        leadMargin,
        momentum,
        gapToClose
      };
    },
    
    competitorComparisonData() {
      if (!this.scanData.execution || !this.scanData.execution.processItems) {
        return {
          labels: [],
          datasets: []
        };
      }
      
      const processItems = this.scanData.execution.processItems;
      const competitors = this.scanData.competitors || [];
      const businessName = this.scanData.businessName || 'Your Business';
      
      // Initialize tracking for all businesses
      const businessStats = {};
      
      // Track main business
      businessStats[businessName] = {
        name: businessName,
        totalRank: 0,
        count: 0,
        isMainBusiness: true
      };
      
      // Track competitors
      competitors.forEach(comp => {
        businessStats[comp.name] = {
          name: comp.name,
          totalRank: 0,
          count: 0,
          isMainBusiness: false
        };
      });
      
      // Process each item
      processItems.forEach(item => {
        // Main business ranking
        const mainRank = parseInt(item.rank || 0);
        if (mainRank > 0 && mainRank <= 20) {
          businessStats[businessName].totalRank += mainRank;
          businessStats[businessName].count++;
        }
        
        // Competitor rankings from result JSON
        if (item.result) {
          try {
            const results = JSON.parse(item.result);
            if (Array.isArray(results)) {
              results.forEach((business, index) => {
                const rank = index + 1;
                
                // Match competitors
                competitors.forEach(comp => {
                  if ((business.place_id && business.place_id === comp.place_id) || 
                      (business.name && comp.name && 
                       business.name.toLowerCase().includes(comp.name.toLowerCase()))) {
                    businessStats[comp.name].totalRank += rank;
                    businessStats[comp.name].count++;
                  }
                });
              });
            }
          } catch (e) {
            console.log('Error parsing result for comparison:', e);
          }
        }
      });
      
      // Calculate averages and prepare chart data
      const chartData = Object.values(businessStats)
        .filter(business => business.count > 0) // Only include businesses with data
        .map(business => ({
          name: business.name,
          avgRank: (business.totalRank / business.count).toFixed(1),
          isMainBusiness: business.isMainBusiness
        }))
        .sort((a, b) => parseFloat(a.avgRank) - parseFloat(b.avgRank)); // Sort by best rank first
      
      return {
        labels: chartData.map(d => d.name),
        datasets: [{
          label: 'Average Ranking Position',
          data: chartData.map(d => parseFloat(d.avgRank)),
          backgroundColor: chartData.map(d => d.isMainBusiness ? '#4f46e5' : '#e5e7eb'),
          borderColor: chartData.map(d => d.isMainBusiness ? '#4338ca' : '#9ca3af'),
          borderWidth: 2
        }]
      };
    },
    
    dynamicKeywordData() {
      // Get unique keywords from processItems
      if (!this.scanData.execution || !this.scanData.execution.processItems) {
        return [];
      }
      
      const processItems = this.scanData.execution.processItems;
      const keywordMap = {};
      
      // Group by keyword
      processItems.forEach(item => {
        const keyword = item.keyword;
        if (!keywordMap[keyword]) {
          keywordMap[keyword] = {
            name: keyword,
            items: [],
            totalRank: 0,
            rankedCount: 0,
            bestPosition: 999,
            locations: new Set(),
            rankedLocations: new Set()
          };
        }
        
        keywordMap[keyword].items.push(item);
        const rank = parseInt(item.rank || 0);
        
        // Track location
        const locationKey = `${item.latitude},${item.longitude}`;
        keywordMap[keyword].locations.add(locationKey);
        
        if (rank > 0 && rank <= 20) {
          keywordMap[keyword].totalRank += rank;
          keywordMap[keyword].rankedCount++;
          keywordMap[keyword].rankedLocations.add(locationKey);
          
          if (rank < keywordMap[keyword].bestPosition) {
            keywordMap[keyword].bestPosition = rank;
          }
        }
      });
      
      // Calculate stats for each keyword
      const keywords = Object.values(keywordMap).map(kw => {
        const avgPosition = kw.rankedCount > 0 
          ? (kw.totalRank / kw.rankedCount).toFixed(1) 
          : 'NR';
        
        const gridCoverage = kw.locations.size > 0
          ? Math.round((kw.rankedLocations.size / kw.locations.size) * 100)
          : 0;
        
        // Determine current position (most recent or best)
        const latestItem = kw.items[kw.items.length - 1];
        const position = parseInt(latestItem.rank || 0) || kw.bestPosition;
        
        // Calculate change (simplified - you'd need historical data for real change)
        let trend = 'stable';
        let change = '0';
        
        if (position < 5) {
          trend = 'up';
          change = '+2';
        } else if (position > 15) {
          trend = 'down';
          change = '-3';
        }
        
        // Extract competitor rankings for this keyword
        const competitorRankings = this.getKeywordCompetitors(kw.items);
        
        return {
          name: kw.name,
          position: position === 999 ? 'NR' : position,
          change: change,
          trend: trend,
          avgPosition: avgPosition,
          bestPosition: kw.bestPosition === 999 ? 'NR' : kw.bestPosition,
          gridCoverage: gridCoverage,
          gridPoints: kw.locations.size,
          rankedPoints: kw.rankedLocations.size,
          improvingPoints: Math.floor(kw.rankedLocations.size * 0.3), // Placeholder
          decliningPoints: Math.floor(kw.rankedLocations.size * 0.1), // Placeholder
          items: kw.items, // Store all processItems for this keyword
          competitors: competitorRankings // Populated from result JSON
        };
      });
      
      return keywords;
    },
    
    competitorRankingDistribution() {
      // If we have fetched competitor analysis data, use it
      if (this.competitorAnalysisData && this.competitorAnalysisData.competitors) {
        return this.competitorAnalysisData.competitors;
      }
      
      // Otherwise, fall back to the original logic using current execution data
      if (!this.scanData.execution || !this.scanData.execution.processItems) {
        return [];
      }
      
      const processItems = this.scanData.execution.processItems;
      const competitors = this.scanData.competitors || [];
      const businessName = this.scanData.businessName || 'Your Business';
      
      // Initialize distribution for main business
      const distribution = {
        [businessName]: {
          name: businessName,
          isYourBusiness: true,
          top3: 0,
          pos4to7: 0,
          pos8to15: 0,
          pos16to20: 0,
          notRanked: 0
        }
      };
      
      // Initialize distribution for each competitor
      competitors.forEach(comp => {
        distribution[comp.place_id] = {
          name: comp.name,
          isYourBusiness: false,
          top3: 0,
          pos4to7: 0,
          pos8to15: 0,
          pos16to20: 0,
          notRanked: 0
        };
      });
      
      // Process each item
      processItems.forEach(item => {
        // Process main business rank
        const mainRank = parseInt(item.rank || 0);
        if (mainRank === 0) {
          distribution[businessName].notRanked++;
        } else if (mainRank >= 1 && mainRank <= 3) {
          distribution[businessName].top3++;
        } else if (mainRank >= 4 && mainRank <= 7) {
          distribution[businessName].pos4to7++;
        } else if (mainRank >= 8 && mainRank <= 15) {
          distribution[businessName].pos8to15++;
        } else if (mainRank >= 16 && mainRank <= 20) {
          distribution[businessName].pos16to20++;
        } else {
          distribution[businessName].notRanked++; // Above 20 is treated as not ranked
        }
        
        // Process competitor ranks from result JSON
        if (item.result) {
          try {
            const results = JSON.parse(item.result);
            
            // Track which competitors were found in this result
            const foundCompetitors = new Set();
            
            if (Array.isArray(results)) {
              results.forEach((business, index) => {
                const businessRank = index + 1; // Position in results (1-based)
                
                // Check each competitor
                competitors.forEach(comp => {
                  // Match by place_id or name
                  if ((business.place_id && business.place_id === comp.place_id) || 
                      (business.name && comp.name && 
                       business.name.toLowerCase().includes(comp.name.toLowerCase()))) {
                    
                    foundCompetitors.add(comp.place_id);
                    
                    // Categorize the rank
                    if (businessRank >= 1 && businessRank <= 3) {
                      distribution[comp.place_id].top3++;
                    } else if (businessRank >= 4 && businessRank <= 7) {
                      distribution[comp.place_id].pos4to7++;
                    } else if (businessRank >= 8 && businessRank <= 15) {
                      distribution[comp.place_id].pos8to15++;
                    } else if (businessRank >= 16 && businessRank <= 20) {
                      distribution[comp.place_id].pos16to20++;
                    }
                  }
                });
              });
            }
            
            // Mark competitors not found in results as not ranked
            competitors.forEach(comp => {
              if (!foundCompetitors.has(comp.place_id)) {
                distribution[comp.place_id].notRanked++;
              }
            });
            
          } catch (e) {
            console.log('Error parsing result JSON:', e);
            // If parsing fails, mark all competitors as not ranked for this item
            competitors.forEach(comp => {
              distribution[comp.place_id].notRanked++;
            });
          }
        } else {
          // No result data, mark all competitors as not ranked
          competitors.forEach(comp => {
            distribution[comp.place_id].notRanked++;
          });
        }
      });
      
      // Convert to array and sort (main business first, then competitors)
      const result = Object.values(distribution).sort((a, b) => {
        if (a.isYourBusiness) return -1;
        if (b.isYourBusiness) return 1;
        return a.name.localeCompare(b.name);
      });
      
      return result;
    }
  },
  async mounted() {
    this.gridId = this.$route.params.id;
    this.executionId = this.$route.params.execution || this.$route.query.execution;
    
    // First, fetch the API key from the store if not already loaded
    await this.$store.dispatch('apiKeys/fetchGoogleMapsKey');
    
    // Fetch grid scan data first (with optional execution ID)
    this.fetchGridScanData();
    
    // Add global functions for HTML onclick handlers
    window.switchKeywordTab = (index, tabName) => this.switchKeywordTab(index, tabName);
    window.updateKeywordData = (index, value) => this.updateKeywordData(index, value);
    window.zoomInKeywordMap = (index) => this.zoomInKeywordMap(index);
    window.zoomOutKeywordMap = (index) => this.zoomOutKeywordMap(index);
    window.resetKeywordMapView = (index) => this.resetKeywordMapView(index);
    
  },
  methods: {
    async fetchCompetitorAnalysis() {
      // Only fetch if we're not in executionId mode
      if (this.executionId) return;
      
      try {
        this.isLoadingCompetitorAnalysis = true;
        
        const response = await axios.get(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/competitor-analysis/${this.gridId}`,
          { params: { timeframe: this.competitorTimeframe } }
        );
        
        if (response.data.success) {
          this.competitorAnalysisData = response.data.data;
          
          // Regenerate the chart with new data
          this.$nextTick(() => {
            this.updateCompetitorComparisonChart();
          });
        }
      } catch (error) {
        console.error('Error fetching competitor analysis:', error);
        this.$swal({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load competitor analysis data'
        });
      } finally {
        this.isLoadingCompetitorAnalysis = false;
      }
    },
    
    async fetchKeywordData(index, period = 30) {
      try {
        const keyword = this.dynamicKeywordData[index];
        if (!keyword) return;
        
        // Encode keyword for URL
        const encodedKeyword = encodeURIComponent(keyword.name);
        
        // Fetch all three endpoints in parallel for better performance
        const [dataResponse, competitorResponse, timelineResponse] = await Promise.all([
          // Main keyword data
          axios.get(
            `${process.env.VUE_APP_API_ENDPOINT}/api/scans/keyword-data/${this.gridId}/${encodedKeyword}`,
            { params: { period } }
          ),
          // Competitor rankings
          axios.get(
            `${process.env.VUE_APP_API_ENDPOINT}/api/scans/keyword-data/${this.gridId}/${encodedKeyword}/competitors`,
            { params: { period } }
          ),
          // Timeline data for charts
          axios.get(
            `${process.env.VUE_APP_API_ENDPOINT}/api/scans/keyword-data/${this.gridId}/${encodedKeyword}/timeline`,
            { params: { period } }
          )
        ]);
        
        if (dataResponse.data.success) {
          // Store the detailed data
          this.$set(this.keywordDetailedData, index, dataResponse.data.data);
          
          // Add competitor data
          if (competitorResponse.data.success) {
            this.keywordDetailedData[index].competitors = competitorResponse.data.data.competitors;
          }
          
          // Add timeline data for charts
          if (timelineResponse.data.success) {
            this.keywordDetailedData[index].timeline = timelineResponse.data.data.timeline;
          }
          
          // Update the keyword's data in the main array
          if (this.dynamicKeywordData[index]) {
            this.dynamicKeywordData[index].locationData = this.keywordDetailedData[index].locationData || [];
            this.dynamicKeywordData[index].competitors = this.keywordDetailedData[index].competitors || [];
            this.dynamicKeywordData[index].timeline = this.keywordDetailedData[index].timeline || [];
          }
        }
      } catch (error) {
        console.error('Error fetching keyword data:', error);
      }
    },
    
    async fetchGridScanData() {
      try {
        this.isLoading = true;
        
        // Include executionId in query params if available
        const url = `${process.env.VUE_APP_API_ENDPOINT}/api/scans/grid-scan-details/${this.gridId}`;
        const params = {};
        if (this.executionId) {
          params.executionId = this.executionId;
        }
        
        const response = await axios.get(url, { params });
        
        if (response.data.success) {
          this.scanData = response.data.data;
          // Ensure competitors are available in scanData
          if (!this.scanData.competitors && response.data.data.competitors) {
            this.scanData.competitors = response.data.data.competitors;
          }
          this.hasData = response.data.data.hasData;
          this.stats = response.data.data.stats || {};
          
          if (this.hasData) {
            // Track scan results viewed event
            this.$posthog.capture('scan_results_viewed', {
              scan_id: this.gridId,
              execution_id: this.executionId || null,
              total_keywords: this.stats.totalKeywords || 0,
              avg_position: this.stats.avgPosition || 0,
              visibility_score: (this.stats.visibility && this.stats.visibility.score) || 0,
              total_locations: this.stats.totalLocations || 0,
              competitors_count: this.competitors ? this.competitors.length : 0,
              scan_type: this.scanData.search_type || 'grid_scan'
            });
            
            // Process the data if execution exists
            this.processItems = (response.data.data.execution && response.data.data.execution.processItems) || [];
            
            this.competitors = response.data.data.competitors || [];
            this.currentCompetitors = response.data.data.competitors || [];
            
            // Update SEO leader data based on actual data
            this.updateSeoLeaderData();
            
            // Load scripts and initialize components
            this.loadChartJS();
            this.loadGoogleMaps();
            
            // Generate competitive analysis after data loads
            setTimeout(() => {
              this.generateCompetitiveAnalysis();
            }, 1000);
            
            // Fetch competitor analysis for default timeframe if not in executionId mode
            if (!this.executionId && this.hasCompetitors) {
              this.fetchCompetitorAnalysis();
            }
          }
        }
      } catch (error) {
        console.error('Error fetching grid scan data:', error);
        this.$swal({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load grid scan data'
        });
      } finally {
        this.isLoading = false;
      }
    },
    
    formatNextRun(dateString) {
      if (!dateString) return 'Not scheduled';
      
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = date - now;
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffHours / 24);
      
      if (diffDays > 1) {
        return `${date.toLocaleDateString()} at ${date.toLocaleTimeString()} (in ${diffDays} days)`;
      } else if (diffDays === 1) {
        return `Tomorrow at ${date.toLocaleTimeString()}`;
      } else if (diffHours > 0) {
        return `Today in ${diffHours} hours`;
      } else {
        return `${date.toLocaleDateString()} at ${date.toLocaleTimeString()}`;
      }
    },
    
    goBack() {
      this.$router.push('/dashboard');
    },
    
    openAddKeywordModal() {
      
      this.showAddKeywordModal = true;
      this.newKeyword = '';
      this.runImmediately = true;
      // Parse existing keywords
      this.currentKeywords = this.scanData.keywords || [];
    
    },
    
    closeAddKeywordModal() {
      this.showAddKeywordModal = false;
      this.newKeyword = '';
      this.isAddingKeyword = false;
    },
    
    async handleAddKeyword() {
      if (!this.newKeyword.trim() || this.isAddingKeyword) return;
      
      // Check if keyword already exists
      if (this.currentKeywords.includes(this.newKeyword.trim())) {
        this.$swal({
          icon: 'warning',
          title: 'Keyword Already Exists',
          text: 'This keyword already exists in the scan',
          confirmButtonColor: '#4F46E5'
        });
        return;
      }
      
      this.isAddingKeyword = true;
      
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/grid-scan/add-keyword`,
          {
            searchId: this.gridId,
            newKeyword: this.newKeyword.trim(),
            runImmediately: this.isScheduledScan ? this.runImmediately : true
          }
        );
        
        if (response.data.success) {
          // Update local keywords list
          this.scanData.keywords = response.data.keywords;
          this.currentKeywords = response.data.keywords;
          
          // Show success message
          this.$swal({
            icon: 'success',
            title: 'Keyword Added Successfully',
            text: response.data.message,
            confirmButtonColor: '#4F46E5'
          });
          
          // Close modal
          this.closeAddKeywordModal();
          
          // If scan was run immediately, refresh the page data
          if (this.isScheduledScan ? this.runImmediately : true) {
            setTimeout(() => {
              this.fetchData();
            }, 2000);
          }
        }
      } catch (error) {
        console.error('Error adding keyword:', error);
        this.$swal({
          icon: 'error',
          title: 'Failed to Add Keyword',
          text: (error.response && error.response.data && error.response.data.message) || 'Failed to add keyword',
          confirmButtonColor: '#4F46E5'
        });
      } finally {
        this.isAddingKeyword = false;
      }
    },
    
    updateSeoLeaderData() {
      // Update SEO leader based on actual process items data
      // This is placeholder - you'll need to calculate from actual data
      if (this.processItems.length > 0) {
        // Group by business and calculate metrics
        // For now keeping the static data
      }
    },
    
    getPositionPercentage(positionType) {
      if (!this.stats.positions || !this.stats.totalProcessItems) return 0;
      
      // Use totalProcessItems as the total count
      const total = this.stats.totalProcessItems;
      
      if (total === 0) return 0;
      
      const value = this.stats.positions[positionType] || 0;
      return ((value / total) * 100).toFixed(1);
    },
    
    loadChartJS() {
      // Check if Chart.js is already loaded
      if (window.Chart) {
        return;
      }

      // Create script tag for Chart.js - using exact same CDN as HTML
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/3.9.1/chart.min.js';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    },
    
    loadGoogleMaps(callback) {
      // Check if Google Maps is already loaded
      if (window.google && window.google.maps) {
        if (callback) callback();
        else this.initMap();
        return;
      }

      // Create script tag for Google Maps with user's API key
      const userApiKey = this.$store.getters['apiKeys/googleMapsKey'];
      if (!userApiKey) {
        console.error('No Google Maps API key configured');

        // Check if in embed mode
        if (this.$store.getters['embed/isEmbedMode']) {
          // In embed mode, show simplified dialog and redirect to scans
          this.$swal({
            icon: 'info',
            title: 'API Key Not Configured',
            text: 'Google Maps API key is not configured.',
            confirmButtonText: 'OK',
            allowOutsideClick: false,
            allowEscapeKey: false
          }).then(() => {
            this.$router.push('/scans');
          });
        } else {
          // Normal mode - show full settings option
          this.$swal({
            icon: 'error',
            title: 'API Key Missing',
            text: 'Google Maps API key not configured. Please configure it in Settings.',
            confirmButtonText: 'OK'
          });
        }
        return;
      }
      
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${userApiKey}&libraries=geometry,places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        if (callback) callback();
        else this.initMap();
      };
      document.head.appendChild(script);
    },
    
    initMap() {
      // Try to get center from business data if available
      let center = { lat: 40.7128, lng: -74.0060 }; // Default to New York City
      
      if (this.scanData && this.scanData.business) {
        const business = this.scanData.business;
        if (business.latitude && business.longitude) {
          center = { 
            lat: parseFloat(business.latitude), 
            lng: parseFloat(business.longitude) 
          };
        } else if (business.location && business.location.lat && business.location.lng) {
          center = { 
            lat: parseFloat(business.location.lat), 
            lng: parseFloat(business.location.lng) 
          };
        }
      }
      
      this.map = new window.google.maps.Map(document.getElementById('googleMap'), {
        zoom: 14,
        center: center,
        mapTypeId: 'roadmap',
        disableDefaultUI: true,
        gestureHandling: 'cooperative',
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          }
        ]
      });

      this.infoWindow = new window.google.maps.InfoWindow({
        pixelOffset: new window.google.maps.Size(150, -20), // Default offset to show on the right
        maxWidth: 420
      });
      
      // Create grid overlay after map is ready
      window.google.maps.event.addListenerOnce(this.map, 'idle', () => {
        this.createGridOverlay();
      });
    },
    
    createGridOverlay() {
      // Clear existing overlays
      this.clearGridOverlays();
      
      // Group process items by lat-long and calculate average ranks
      const groupedLocations = this.groupProcessItemsByLocation();
      
      if (Object.keys(groupedLocations).length === 0) {
        // No data to display on map
        return;
      }
      
      // Create markers for each unique location
      Object.entries(groupedLocations).forEach(([locationKey, locationData]) => {
        const position = new window.google.maps.LatLng(locationData.lat, locationData.lng);
        
        // Calculate average rank (treating rank 0 as 21+ for averaging)
        const avgRank = locationData.avgRank;
        const color = this.getRankColor(avgRank);
        const rankDisplay = avgRank > 20 ? '20+' : Math.round(avgRank).toString();
        
        const marker = this.createGridMarker(position, color, rankDisplay, locationData);
        this.gridOverlays.push(marker);
      });
      
      // Calculate bounds that encompass all markers
      if (this.gridOverlays.length > 0) {
        const markerBounds = new window.google.maps.LatLngBounds();
        this.gridOverlays.forEach(marker => {
          markerBounds.extend(marker.getPosition());
        });
        
        // Add padding around the markers
        const ne = markerBounds.getNorthEast();
        const sw = markerBounds.getSouthWest();
        
        const latPadding = (ne.lat() - sw.lat()) * 0.02;
        const lngPadding = (ne.lng() - sw.lng()) * 0.02;
        
        // Create the blue rectangle overlay
        const backgroundBounds = new window.google.maps.LatLngBounds(
          new window.google.maps.LatLng(sw.lat() - latPadding, sw.lng() - lngPadding),
          new window.google.maps.LatLng(ne.lat() + latPadding, ne.lng() + lngPadding)
        );

        this.gridRectangle = new window.google.maps.Rectangle({
          bounds: backgroundBounds,
          fillColor: '#4f46e5',
          fillOpacity: 0.15,
          strokeColor: '#4f46e5',
          strokeOpacity: 0.3,
          strokeWeight: 2,
          map: this.map
        });
        
        // Fit map to show all markers
        this.map.fitBounds(markerBounds);
        // Add a bit more zoom out to show context
        setTimeout(() => {
          const currentZoom = this.map.getZoom();
          if (currentZoom > 14) {
            this.map.setZoom(14);
          }
        }, 300);
      }
    },
    
    groupProcessItemsByLocation() {
      const grouped = {};
      
      if (!this.scanData.execution || !this.scanData.execution.processItems) {
        return grouped;
      }
      
      const processItems = this.scanData.execution.processItems;
      const competitors = this.scanData.competitors || [];
      
      processItems.forEach(item => {
        const locationKey = `${item.latitude},${item.longitude}`;
        
        if (!grouped[locationKey]) {
          grouped[locationKey] = {
            lat: parseFloat(item.latitude),
            lng: parseFloat(item.longitude),
            keywords: [],
            totalRank: 0,
            rankedCount: 0,
            avgRank: 0,
            competitorRanks: {}
          };
          
          // Initialize competitor tracking
          competitors.forEach(comp => {
            grouped[locationKey].competitorRanks[comp.place_id] = {
              name: comp.name,
              totalRank: 0,
              count: 0,
              avgRank: 0
            };
          });
        }
        
        // Process rank for this keyword at this location
        const rank = parseInt(item.rank || 0);
        const effectiveRank = rank === 0 ? 21 : rank; // Treat rank 0 as 21 for averaging
        
        grouped[locationKey].keywords.push({
          keyword: item.keyword,
          rank: rank,
          effectiveRank: effectiveRank
        });
        
        grouped[locationKey].totalRank += effectiveRank;
        grouped[locationKey].rankedCount++;
        
        // Process competitor ranks from result JSON
        if (item.result) {
          try {
            const results = JSON.parse(item.result);
            if (Array.isArray(results)) {
              results.forEach((business, index) => {
                const businessRank = index + 1;
                
                // Check each competitor
                competitors.forEach(comp => {
                  if (business.place_id === comp.place_id || 
                      (business.name && comp.name && business.name.toLowerCase().includes(comp.name.toLowerCase()))) {
                    grouped[locationKey].competitorRanks[comp.place_id].totalRank += businessRank;
                    grouped[locationKey].competitorRanks[comp.place_id].count++;
                  }
                });
              });
            }
          } catch (e) {
            console.log('Error parsing result JSON:', e);
          }
        }
      });
      
      // Calculate averages
      Object.values(grouped).forEach(location => {
        location.avgRank = location.rankedCount > 0 ? location.totalRank / location.rankedCount : 21;
        
        // Calculate competitor averages
        Object.values(location.competitorRanks).forEach(comp => {
          comp.avgRank = comp.count > 0 ? comp.totalRank / comp.count : 21;
        });
      });
      
      return grouped;
    },
    
    createGridMarker(position, color, rankDisplay, data) {
      const marker = new window.google.maps.Marker({
        position: position,
        map: this.map,
        icon: {
          url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(this.createMarkerSVG(color, rankDisplay))}`,
          scaledSize: new window.google.maps.Size(32, 48),
          anchor: new window.google.maps.Point(16, 48) // Anchor at the bottom point of the pin
        }
      });

      marker.addListener('click', () => {
        this.showLocationDetails(data, position);
      });

      return marker;
    },
    
    createMarkerSVG(color, rankDisplay) {
      return `
        <svg width="32" height="48" viewBox="0 0 32 48" xmlns="http://www.w3.org/2000/svg">
          <!-- Outer pin shape with shadow -->
          <ellipse cx="16" cy="46" rx="8" ry="2" fill="#000000" opacity="0.2"/>
          <!-- Pin shape path -->
          <path d="M16 0C7.2 0 0 7.2 0 16c0 12 16 32 16 32s16-20 16-32C32 7.2 24.8 0 16 0z" 
                fill="${color}" 
                stroke="#FFFFFF" 
                stroke-width="1.5"/>
          <!-- Inner circle for text background -->
          <circle cx="16" cy="16" r="11" fill="#FFFFFF" opacity="0.95"/>
          <!-- Text -->
          <text x="16" y="20" text-anchor="middle" fill="${color}" font-family="Rubik, Arial, sans-serif" font-size="11" font-weight="700">${rankDisplay}</text>
        </svg>
      `;
    },
    
    getRankColor(rank) {
      if (rank <= 3) return '#10b981';
      if (rank <= 7) return '#4f46e5';
      if (rank <= 15) return '#FFB443';
      if (rank <= 20) return '#ef4444';
      return '#757B89';
    },
    
    createInfoWindowContent(data, position) {
      const avgRankDisplay = data.avgRank > 20 ? '20+' : Math.round(data.avgRank).toFixed(1);
      const coords = `${position.lat().toFixed(6)}, ${position.lng().toFixed(6)}`;
      
      // Build keyword list items HTML with ranking number
      let keywordHTML = '';
      if (data.keywords && data.keywords.length > 0) {
        keywordHTML = data.keywords.map((kw, index) => {
          const rankDisplay = kw.rank === 0 ? '20+' : kw.rank;
          const rankColor = this.getRankColor(kw.effectiveRank);
          return `
            <div style="display: flex; align-items: flex-start; padding: 12px; border-bottom: 1px solid #e5e7eb; background: ${index % 2 === 0 ? '#ffffff' : '#f9fafb'};">
              <div style="min-width: 32px; height: 32px; background: ${rankColor}; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 14px; margin-right: 12px;">
                ${index + 1}
              </div>
              <div style="flex: 1;">
                <div style="font-weight: 600; font-size: 14px; color: #111827; margin-bottom: 4px;">
                  ${kw.keyword}
                </div>
                <div style="display: flex; align-items: center; gap: 16px;">
                  <span style="font-size: 12px; color: #6b7280;">
                    Rank: <span style="font-weight: 600; color: ${rankColor};">#${rankDisplay}</span>
                  </span>
                </div>
              </div>
            </div>
          `;
        }).join('');
      }
      
      // Build competitor list HTML if available
      let competitorHTML = '';
      if (data.competitorRanks && Object.keys(data.competitorRanks).length > 0) {
        const sortedCompetitors = Object.values(data.competitorRanks)
          .filter(comp => comp.count > 0) // Only show competitors that appear in results
          .sort((a, b) => a.avgRank - b.avgRank) // Sort by best average rank
          .slice(0, 5); // Show top 5 competitors
        
        if (sortedCompetitors.length > 0) {
          competitorHTML = `
            <div style="margin-top: 16px; padding-top: 16px; border-top: 2px solid #e5e7eb;">
              <div style="padding: 12px 12px 8px 12px; background: #fef2f2;">
                <div style="font-weight: 700; font-size: 14px; color: #111827;">Top Competitors</div>
              </div>
              ${sortedCompetitors.map((comp, index) => {
                const avgDisplay = comp.avgRank > 20 ? '20+' : comp.avgRank.toFixed(1);
                return `
                  <div style="display: flex; align-items: center; padding: 10px 12px; border-bottom: 1px solid #fee2e2; background: ${index % 2 === 0 ? '#fff5f5' : '#ffffff'};">
                    <div style="min-width: 24px; height: 24px; background: #dc2626; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 600; font-size: 11px; margin-right: 10px;">
                      ${index + 1}
                    </div>
                    <div style="flex: 1;">
                      <div style="font-size: 13px; font-weight: 500; color: #111827;">${comp.name}</div>
                      <div style="font-size: 11px; color: #6b7280;">Average Rank: #${avgDisplay}</div>
                    </div>
                  </div>
                `;
              }).join('')}
            </div>
          `;
        }
      }

      return `
        <div style="min-width: 360px; max-width: 420px; background: white; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); font-family: 'Rubik', sans-serif;">
          <!-- Header -->
          <div style="background: linear-gradient(135deg, #667EEA 0%, #764BA2 100%); padding: 16px;">
            <div style="font-weight: 700; font-size: 16px; color: white; margin-bottom: 4px;">
              Location Performance
            </div>
            <div style="font-size: 12px; color: rgba(255, 255, 255, 0.9); margin-bottom: 12px;">
              📍 ${coords}
            </div>
            <div style="display: flex; align-items: baseline; gap: 8px;">
              <span style="font-size: 32px; font-weight: 700; color: white;">#${avgRankDisplay}</span>
              <span style="font-size: 14px; color: rgba(255, 255, 255, 0.9);">Average Rank</span>
            </div>
            <div style="font-size: 12px; color: rgba(255, 255, 255, 0.8); margin-top: 4px;">
              Across ${data.keywords.length} keyword${data.keywords.length > 1 ? 's' : ''}
            </div>
          </div>
          
          <!-- Keyword Rankings List -->
          <div style="background: white;">
            <div style="padding: 12px 12px 8px 12px; background: #f9fafb; border-bottom: 1px solid #e5e7eb;">
              <div style="font-weight: 700; font-size: 14px; color: #111827;">Keyword Rankings</div>
            </div>
            <div style="max-height: 300px; overflow-y: auto;">
              ${keywordHTML}
            </div>
            ${competitorHTML}
          </div>
        </div>
      `;
    },
    
    clearGridOverlays() {
      this.gridOverlays.forEach(overlay => {
        overlay.setMap(null);
      });
      this.gridOverlays = [];
      
      if (this.gridRectangle) {
        this.gridRectangle.setMap(null);
        this.gridRectangle = null;
      }
    },
    
    showLocationDetails(data, position) {
      // Close any existing InfoWindow
      if (this.infoWindow) {
        this.infoWindow.close();
      }
      
      // Format coordinates
      this.selectedLocationCoords = `${position.lat().toFixed(6)}, ${position.lng().toFixed(6)}`;
      
      // Prepare location data
      const avgRankDisplay = data.avgRank > 20 ? '20+' : Math.round(data.avgRank).toFixed(1);
      
      // Prepare competitors data
      let competitors = [];
      if (data.competitorRanks && Object.keys(data.competitorRanks).length > 0) {
        competitors = Object.values(data.competitorRanks)
          .filter(comp => comp.count > 0)
          .sort((a, b) => a.avgRank - b.avgRank)
          .slice(0, 5)
          .map(comp => ({
            name: comp.name,
            avgDisplay: comp.avgRank > 20 ? '20+' : comp.avgRank.toFixed(1)
          }));
      }
      
      this.selectedLocationData = {
        avgRankDisplay,
        keywords: data.keywords || [],
        competitors
      };
      
      // Show the panel
      this.showLocationPanel = true;
    },
    
    closeLocationPanel() {
      this.showLocationPanel = false;
      this.selectedLocationData = null;
      this.selectedLocationCoords = '';
    },
    
    zoomInMap() {
      if (this.map) {
        this.map.setZoom(this.map.getZoom() + 1);
        setTimeout(() => this.createGridOverlay(), 300);
      }
    },
    
    zoomOutMap() {
      if (this.map) {
        this.map.setZoom(this.map.getZoom() - 1);
        setTimeout(() => this.createGridOverlay(), 300);
      }
    },
    
    resetMapView() {
      if (this.map) {
        // Try to get center from business data if available
        let center = { lat: 40.7128, lng: -74.0060 }; // Default
        
        if (this.scanData && this.scanData.business) {
          const business = this.scanData.business;
          if (business.latitude && business.longitude) {
            center = { 
              lat: parseFloat(business.latitude), 
              lng: parseFloat(business.longitude) 
            };
          } else if (business.location && business.location.lat && business.location.lng) {
            center = { 
              lat: parseFloat(business.location.lat), 
              lng: parseFloat(business.location.lng) 
            };
          }
        }
        
        this.map.setZoom(14);
        this.map.setCenter(center);
        
        // If we have markers, fit bounds to show all of them
        if (this.gridOverlays.length > 0) {
          const bounds = new window.google.maps.LatLngBounds();
          this.gridOverlays.forEach(marker => {
            bounds.extend(marker.getPosition());
          });
          this.map.fitBounds(bounds);
          setTimeout(() => {
            const currentZoom = this.map.getZoom();
            if (currentZoom > 14) {
              this.map.setZoom(14);
            }
          }, 300);
        }
        
        setTimeout(() => this.createGridOverlay(), 300);
      }
    },
    
    generateKeywordCards() {
      const grid = document.getElementById('keywordGrid');
      if (!grid) return;
      
      grid.innerHTML = '';
      
      this.keywordData.forEach((keyword, index) => {
        const card = document.createElement('div');
        card.className = 'keyword-card';
        card.onclick = () => this.toggleKeywordDetails(index);
        
        let trendClass = 'rank-stable';
        if (keyword.trend === 'up') trendClass = 'rank-up';
        else if (keyword.trend === 'down') trendClass = 'rank-down';
        
        card.innerHTML = this.getKeywordCardHTML(keyword, index, trendClass);
        grid.appendChild(card);
      });
    },
    
    getKeywordCardHTML(keyword, index, trendClass) {
      const competitorsHTML = keyword.competitors.map(comp => `
        <div class="competitor-item" style="background: ${comp.name === 'Your Business' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255, 255, 255, 0.1)'}">
          <div class="competitor-info">
            <span class="competitor-name">${comp.name}</span>
            <span class="competitor-duration">${comp.duration}</span>
          </div>
          <div class="competitor-rank">
            <span class="competitor-position">#${comp.position}</span>
            <span class="rank-change ${comp.change.includes('+') ? 'rank-up' : comp.change.includes('-') ? 'rank-down' : 'rank-stable'}">${comp.change}</span>
          </div>
        </div>
      `).join('');

      return `
        <div class="keyword-header">
          <div class="keyword-name">${keyword.name}</div>
          <div class="keyword-rank">
            <span class="rank-position">#${keyword.position}</span>
            <span class="rank-change ${trendClass}">${keyword.change}</span>
          </div>
        </div>
        
        <div class="keyword-stats">
          <div class="stat-item">
            <div class="stat-value">${keyword.avgPosition}</div>
            <div class="stat-label-small">Avg Position</div>
          </div>
          <div class="stat-item">
            <div class="stat-value">${keyword.bestPosition}</div>
            <div class="stat-label-small">Best Position</div>
          </div>
          <div class="stat-item">
            <div class="stat-value">${keyword.gridCoverage}%</div>
            <div class="stat-label-small">Grid Coverage</div>
          </div>
        </div>
        
        <div class="keyword-details">
          <div class="keyword-period-controls">
            <h4>Detailed Analysis</h4>
            <select class="keyword-period-select" id="keyword-period-${index}" onchange="updateKeywordData(${index}, this.value)">
              <option value="7">Last 7 Days</option>
              <option value="30" selected>Last 30 Days</option>
              <option value="90">Last 90 Days</option>
              <option value="180">Last 6 Months</option>
            </select>
          </div>

          <div class="keyword-tabs">
            <button class="keyword-tab active" data-tab="charts" data-index="${index}">
              📊 Charts
            </button>
            <button class="keyword-tab" data-tab="heatmap" data-index="${index}">
              🗺️ Heatmap
            </button>
            <button class="keyword-tab" data-tab="competitors" data-index="${index}">
              🏆 Competitors
            </button>
          </div>

          <!-- Charts Tab -->
          <div class="keyword-tab-content active" id="charts-tab-${index}">
            <div class="charts-tab-content">
              <div class="chart-container">
                <canvas id="chart-${index}" width="400" height="300"></canvas>
              </div>
              <div class="chart-stats">
                <h5>Performance Metrics</h5>
                <div class="chart-stat-item">
                  <span class="chart-stat-label">Current Position</span>
                  <span class="chart-stat-value">#${keyword.position}</span>
                </div>
                <div class="chart-stat-item">
                  <span class="chart-stat-label">Best Position</span>
                  <span class="chart-stat-value">#${keyword.bestPosition}</span>
                </div>
                <div class="chart-stat-item">
                  <span class="chart-stat-label">Average Position</span>
                  <span class="chart-stat-value">${keyword.avgPosition}</span>
                </div>
                <div class="chart-stat-item">
                  <span class="chart-stat-label">Grid Coverage</span>
                  <span class="chart-stat-value">${keyword.gridCoverage}%</span>
                </div>
                <div class="chart-stat-item">
                  <span class="chart-stat-label">Trend</span>
                  <span class="chart-stat-value ${keyword.trend === 'up' ? 'rank-up' : keyword.trend === 'down' ? 'rank-down' : 'rank-stable'}">${keyword.change}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Heatmap Tab -->
          <div class="keyword-tab-content" id="heatmap-tab-${index}">
            <div class="heatmap-tab-content">
              <div class="keyword-heatmap-legend">
                <div class="legend-item">
                  <div class="legend-color rank-1-3"></div>
                  <span>Positions 1-3</span>
                </div>
                <div class="legend-item">
                  <div class="legend-color rank-4-7"></div>
                  <span>Positions 4-7</span>
                </div>
                <div class="legend-item">
                  <div class="legend-color rank-8-15"></div>
                  <span>Positions 8-15</span>
                </div>
                <div class="legend-item">
                  <div class="legend-color rank-16-plus"></div>
                  <span>Position 16+</span>
                </div>
                <div class="legend-item">
                  <div class="legend-color not-ranked"></div>
                  <span>Not Ranked</span>
                </div>
              </div>
              <div class="keyword-heatmap-container">
                <div class="keyword-google-map" id="keyword-heatmap-${index}"></div>
                
                <!-- Keyword Map Controls -->
                <div class="keyword-map-controls">
                  <button class="keyword-map-control-btn" onclick="zoomInKeywordMap(${index})" title="Zoom In">+</button>
                  <button class="keyword-map-control-btn" onclick="zoomOutKeywordMap(${index})" title="Zoom Out">−</button>
                  <button class="keyword-map-control-btn" onclick="resetKeywordMapView(${index})" title="Reset">⌂</button>
                </div>
                
                <!-- Keyword Grid Info -->
                <div class="keyword-grid-info">
                  <div class="keyword-grid-info-title">"${keyword.name}"</div>
                  <div class="keyword-grid-info-details">Grid: 5×5 • Avg: #${keyword.avgPosition}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Competitors Tab -->
          <div class="keyword-tab-content" id="competitors-tab-${index}">
            <div class="competitors-tab-content">
              <div class="competitors-section">
                <h5>Current Rankings for "${keyword.name}"</h5>
                <div id="keyword-competitors-${index}">
                  ${competitorsHTML}
                </div>
              </div>
            </div>
          </div>
        </div>
      `;
    },
    
    getRankChangeClass(trend, change) {
      if (trend === 'up' || (change && change.includes('+'))) return 'rank-up';
      if (trend === 'down' || (change && change.includes('-'))) return 'rank-down';
      return 'rank-stable';
    },
    
    getActiveTab(index) {
      return this.keywordActiveTabs[index] || 'charts';
    },
    
    async updateKeywordData(index, period) {
      // Store the selected period for this keyword
      this.$set(this.keywordSelectedPeriods, index, parseInt(period));
      
      // Show loading state
      this.$set(this.keywordLoadingStates, index, true);
      
      try {
        // Fetch fresh data from backend with period filter
        await this.fetchKeywordData(index, period);
        
        // Force update all visualizations with new data
        this.$nextTick(() => {
          // Update charts with new timeline data
          this.updateKeywordCharts(index, period);
          
          // Update heatmap with new location data
          this.updateKeywordHeatmap(index, period);
          
          // Update competitor rankings if tab is active
          if (this.getActiveTab(index) === 'competitors') {
            this.updateCompetitorRankings(index);
          }
        });
      } finally {
        // Hide loading state
        this.$set(this.keywordLoadingStates, index, false);
      }
    },
    
    updateCompetitorRankings(index) {
      // Update the competitor rankings display with new data
      const keywordData = this.keywordDetailedData[index];
      if (!keywordData || !keywordData.competitors) {
        return;
      }
      
      // The competitor data is already updated in keywordDetailedData
      // Vue's reactivity should handle the display update
      // Force update if needed
      this.$forceUpdate();
    },
    
    getFilteredKeywordData(keyword, periodDays) {
      // For now, return all items since we don't have timestamp data
      // In production, you'd filter by date
      return keyword.items;
    },
    
    updateKeywordCharts(index, period) {
      const keywordData = this.keywordDetailedData[index] || this.dynamicKeywordData[index];
      if (!keywordData || !window.Chart) return;
      
      // Use timeline data from backend if available
      let chartData;
      if (keywordData.timeline && keywordData.timeline.length > 0) {
        // Use real timeline data from backend
        const timeline = keywordData.timeline;
        chartData = {
          labels: timeline.map(t => {
            const date = new Date(t.date);
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          }),
          rankData: timeline.map(t => t.avgRank === 'NR' ? 21 : parseFloat(t.avgRank)),
          coverageData: [
            timeline.filter(t => t.rankedCount > 0).length,
            timeline.filter(t => t.notRankedCount > 0).length
          ]
        };
      } else {
        // Fallback to prepared chart data
        chartData = this.prepareChartData(keywordData.items || [], period);
      }
      
      // Update rank trend chart
      const rankCanvas = document.getElementById(`keyword-rank-chart-${index}`);
      if (rankCanvas && this.keywordCharts[index]) {
        // Update existing chart data
        if (this.keywordCharts[index].rankChart) {
          this.keywordCharts[index].rankChart.data.labels = chartData.labels;
          this.keywordCharts[index].rankChart.data.datasets[0].data = chartData.rankData;
          this.keywordCharts[index].rankChart.options.plugins.title.text = `Rank Trend (Last ${period} Days)`;
          this.keywordCharts[index].rankChart.update();
        }
        
        // Update grid coverage chart
        if (this.keywordCharts[index].gridChart) {
          // Calculate coverage based on stats if available
          const stats = keywordData.stats;
          if (stats) {
            const coveragePercentage = Math.round((stats.rankedCount / (stats.rankedCount + stats.notRankedCount)) * 100);
            this.keywordCharts[index].gridChart.data.datasets[0].data = [
              coveragePercentage,
              100 - coveragePercentage
            ];
          } else {
            this.keywordCharts[index].gridChart.data.datasets[0].data = chartData.coverageData;
          }
          this.keywordCharts[index].gridChart.update();
        }
      }
    },
    
    prepareChartData(items, periodDays) {
      // Generate labels based on period
      const labels = [];
      const rankData = [];
      const coverageData = [];
      
      const period = parseInt(periodDays);
      let divisions = 4; // Default to 4 data points
      
      if (period <= 7) {
        divisions = 7; // Daily for last 7 days
      } else if (period <= 30) {
        divisions = 4; // Weekly for last month
      } else if (period <= 90) {
        divisions = 12; // Weekly for 3 months
      } else {
        divisions = 6; // Monthly for 6 months
      }
      
      // Create labels and simulate data (in production, group by actual dates)
      for (let i = 0; i < divisions; i++) {
        if (period <= 7) {
          labels.push(`Day ${i + 1}`);
        } else if (period <= 30) {
          labels.push(`Week ${i + 1}`);
        } else {
          labels.push(`Month ${i + 1}`);
        }
        
        // Calculate average rank for this period segment
        const segmentItems = items.slice(
          Math.floor(i * items.length / divisions),
          Math.floor((i + 1) * items.length / divisions)
        );
        
        let totalRank = 0;
        let rankedCount = 0;
        let coverage = 0;
        
        segmentItems.forEach(item => {
          const rank = parseInt(item.rank || 0);
          if (rank > 0 && rank <= 20) {
            totalRank += rank;
            rankedCount++;
          }
        });
        
        const avgRank = rankedCount > 0 ? totalRank / rankedCount : 20;
        coverage = rankedCount > 0 ? (rankedCount / segmentItems.length) * 100 : 0;
        
        rankData.push(avgRank);
        coverageData.push(coverage);
      }
      
      return { labels, rankData, coverageData };
    },
    
    zoomInKeywordMap(index) {
      const map = this.keywordMaps[index];
      if (map) {
        map.setZoom(map.getZoom() + 1);
      }
    },
    
    zoomOutKeywordMap(index) {
      const map = this.keywordMaps[index];
      if (map) {
        map.setZoom(map.getZoom() - 1);
      }
    },
    
    resetKeywordMapView(index) {
      const map = this.keywordMaps[index];
      if (map) {
        map.setZoom(14);
        map.setCenter({ lat: 28.5887354, lng: 77.0829215 });
      }
    },
    
    closeKeywordDetails() {
      this.expandedKeyword = null;
    },
    
    async toggleKeywordDetails(index) {
      if (this.expandedKeyword === index) {
        this.closeKeywordDetails();
        return;
      }
      
      // Set the new expanded keyword
      this.expandedKeyword = index;
      
      // Initialize default tab
      if (!this.keywordActiveTabs[index]) {
        this.$set(this.keywordActiveTabs, index, 'charts');
      }
      
      // Fetch keyword-specific data from backend
      await this.fetchKeywordData(index);
      
      // Initialize charts and tab functionality
      this.$nextTick(() => {
        this.initKeywordCharts(index);
      });
    },
    
    setupTabListeners(index) {
      const card = document.querySelectorAll('.keyword-card')[index];
      const tabs = card.querySelectorAll('.keyword-tab');
      const contents = card.querySelectorAll('.keyword-tab-content');
      
      tabs.forEach(tab => {
        tab.onclick = (e) => {
          e.stopPropagation();
          const tabName = tab.dataset.tab;
          
          // Remove active from all tabs and contents
          tabs.forEach(t => t.classList.remove('active'));
          contents.forEach(c => c.classList.remove('active'));
          
          // Add active to clicked tab and corresponding content
          tab.classList.add('active');
          const content = card.querySelector(`#${tabName}-tab-${index}`);
          if (content) {
            content.classList.add('active');
          }
        };
      });
    },
    
    initKeywordCharts(index) {
      // Wait for Chart.js to load if not already loaded
      if (!window.Chart) {
        setTimeout(() => this.initKeywordCharts(index), 500);
        return;
      }
      
      const keyword = this.dynamicKeywordData[index];
      
      // Initialize charts storage if not exists
      if (!this.keywordCharts) {
        this.keywordCharts = {};
      }
      
      // Destroy existing charts for this keyword if they exist
      if (this.keywordCharts[index]) {
        if (this.keywordCharts[index].rankChart) {
          this.keywordCharts[index].rankChart.destroy();
        }
        if (this.keywordCharts[index].gridChart) {
          this.keywordCharts[index].gridChart.destroy();
        }
      }
      
      this.keywordCharts[index] = {};
      
      // Create Rank Trend Chart
      const rankCanvas = document.getElementById(`keyword-rank-chart-${index}`);
      if (rankCanvas) {
        const rankCtx = rankCanvas.getContext('2d');
        this.keywordCharts[index].rankChart = new Chart(rankCtx, {
          type: 'line',
          data: {
            labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
            datasets: [{
              label: 'Rank Position',
              data: this.generateTrendData(keyword.position, 4),
              borderColor: '#4f46e5',
              backgroundColor: 'rgba(79, 70, 229, 0.1)',
              tension: 0.4,
              fill: true,
              pointRadius: 4,
              pointBackgroundColor: '#4f46e5',
              pointBorderColor: '#FFFFFF',
              pointBorderWidth: 2
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              title: {
                display: true,
                text: 'Rank Trend (Last 30 Days)',
                color: '#FFFFFF',
                font: { size: 14, weight: 'bold' }
              },
              legend: { display: false }
            },
            scales: {
              y: {
                reverse: true,
                min: 1,
                max: 20,
                ticks: { 
                  color: 'rgba(255, 255, 255, 0.8)',
                  stepSize: 1
                },
                grid: { color: 'rgba(255, 255, 255, 0.1)' }
              },
              x: {
                ticks: { color: 'rgba(255, 255, 255, 0.8)' },
                grid: { color: 'rgba(255, 255, 255, 0.1)' }
              }
            }
          }
        });
      }
      
      // Create Grid Coverage Chart
      const gridCanvas = document.getElementById(`keyword-grid-chart-${index}`);
      if (gridCanvas) {
        const gridCtx = gridCanvas.getContext('2d');
        this.keywordCharts[index].gridChart = new Chart(gridCtx, {
          type: 'doughnut',
          data: {
            labels: ['Ranked', 'Not Ranked'],
            datasets: [{
              data: [keyword.gridCoverage, 100 - keyword.gridCoverage],
              backgroundColor: ['#10b981', 'rgba(255, 255, 255, 0.2)'],
              borderWidth: 0
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              title: {
                display: true,
                text: 'Grid Coverage',
                color: '#FFFFFF',
                font: { size: 14, weight: 'bold' }
              },
              legend: {
                display: true,
                position: 'bottom',
                labels: { color: 'rgba(255, 255, 255, 0.8)' }
              }
            }
          }
        });
      }
      
    },
    
    generateTrendData(currentPosition, points) {
      const data = [];
      let pos = typeof currentPosition === 'number' ? currentPosition : 
                (currentPosition === 'NR' ? 21 : parseInt(currentPosition));
      
      // Generate realistic trend data
      for (let i = 0; i < points - 1; i++) {
        // Create more realistic variations based on current position
        let variation = 0;
        if (pos <= 5) {
          // Top positions have smaller variations
          variation = Math.floor(Math.random() * 3) - 1;
        } else if (pos <= 10) {
          // Mid positions have moderate variations
          variation = Math.floor(Math.random() * 4) - 2;
        } else {
          // Lower positions have larger variations
          variation = Math.floor(Math.random() * 6) - 3;
        }
        
        pos = Math.max(1, Math.min(20, pos + variation));
        data.push(pos);
      }
      
      // End with current position
      data.push(typeof currentPosition === 'number' ? currentPosition : 
                (currentPosition === 'NR' ? 21 : parseInt(currentPosition)));
      return data;
    },
    
    
    generateCompetitiveAnalysis() {
      // Simply generate the chart with dynamic data
      this.generateSimpleComparisonChart();
    },
    
    generateRankingsTable(stats) {
      const table = document.getElementById('competitorRankingsTable');
      if (!table) return;

      let html = `
        <div style="display: grid; grid-template-columns: 2fr repeat(5, 1fr); gap: 8px; font-weight: 600; padding: 12px; background: #f3f4f6; border-radius: 8px; margin-bottom: 12px; font-size: 0.875rem; color: #374151;">
          <div>Competitor</div>
          <div style="text-align: center;">Top 3</div>
          <div style="text-align: center;">4-7</div>
          <div style="text-align: center;">8-15</div>
          <div style="text-align: center;">16+</div>
          <div style="text-align: center;">Not Ranked</div>
        </div>
      `;

      const sortedStats = Object.values(stats).sort((a, b) => b.topPositions - a.topPositions);

      sortedStats.forEach(competitor => {
        const isYourBusiness = competitor.name === 'Your Business';
        html += `
          <div style="display: grid; grid-template-columns: 2fr repeat(5, 1fr); gap: 8px; padding: 12px; border-radius: 6px; margin-bottom: 8px; transition: background 0.2s ease; ${isYourBusiness ? 'background: #e0e7ff; border: 1px solid #c7d2fe;' : 'background: white; border: 1px solid #E5E7EB;'} font-size: 0.875rem;">
            <div style="font-weight: ${isYourBusiness ? '600' : '500'}; color: ${isYourBusiness ? '#4338ca' : '#374151'};">${competitor.name}</div>
            <div style="text-align: center;"><span style="background: #dcfce7; color: #16a34a; padding: 2px 8px; border-radius: 12px; font-size: 0.75rem; font-weight: 600;">${competitor['1-3']}</span></div>
            <div style="text-align: center;"><span style="background: #dbeafe; color: #2563eb; padding: 2px 8px; border-radius: 12px; font-size: 0.75rem; font-weight: 600;">${competitor['4-7']}</span></div>
            <div style="text-align: center;"><span style="background: #fef3c7; color: #d97706; padding: 2px 8px; border-radius: 12px; font-size: 0.75rem; font-weight: 600;">${competitor['8-15']}</span></div>
            <div style="text-align: center;"><span style="background: #fecaca; color: #dc2626; padding: 2px 8px; border-radius: 12px; font-size: 0.75rem; font-weight: 600;">${competitor['16-plus']}</span></div>
            <div style="text-align: center;"><span style="background: #f3f4f6; color: #757B89; padding: 2px 8px; border-radius: 12px; font-size: 0.75rem; font-weight: 600;">${competitor['not-ranked']}</span></div>
          </div>
        `;
      });

      table.innerHTML = html;
    },
    
    generateSEOLeader(stats) {
      const leaderCard = document.getElementById('seoLeaderCard');
      const leaderStatsEl = document.getElementById('leaderStats');
      if (!leaderCard || !leaderStatsEl) return;

      const competitors = Object.values(stats).map(competitor => ({
        ...competitor,
        topPositionsScore: competitor['1-3']
      }));

      competitors.sort((a, b) => {
        if (b.topPositionsScore !== a.topPositionsScore) {
          return b.topPositionsScore - a.topPositionsScore;
        }
        return parseFloat(a.avgPosition) - parseFloat(b.avgPosition);
      });

      const leader = competitors[0];
      const isYourBusiness = leader.name === 'Your Business';
      
      leaderCard.innerHTML = `
        <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 12px;">
          <div style="font-size: 1.125rem; font-weight: 600; color: #19233A;">${leader.name}</div>
          <div style="background: ${isYourBusiness ? '#dcfce7' : '#f3f4f6'}; color: ${isYourBusiness ? '#16a34a' : '#757B89'}; padding: 4px 8px; border-radius: 6px; font-size: 0.75rem; font-weight: 600; text-transform: uppercase;">
            ${isYourBusiness ? 'YOU LEAD' : 'LEADER'}
          </div>
        </div>
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px;">
          <div style="background: #f9fafb; padding: 12px; border-radius: 8px;">
            <div style="font-size: 1.5rem; font-weight: 700; color: #10b981;">${leader['1-3']}</div>
            <div style="font-size: 0.75rem; color: #757B89;">Top 3 Rankings</div>
          </div>
          <div style="background: #f9fafb; padding: 12px; border-radius: 8px;">
            <div style="font-size: 1.5rem; font-weight: 700; color: #4f46e5;">${leader.avgPosition}</div>
            <div style="font-size: 0.75rem; color: #757B89;">Avg Position</div>
          </div>
        </div>
      `;

      leaderStatsEl.innerHTML = `
        <div style="margin-top: 16px; padding-top: 16px; border-top: 1px solid #E5E7EB;">
          <div style="font-size: 0.875rem; font-weight: 600; color: #374151; margin-bottom: 8px;">Performance Breakdown</div>
          <div style="display: flex; justify-content: space-between; font-size: 0.75rem; margin-bottom: 4px;">
            <span style="color: #757B89;">Top 3 Positions</span>
            <span style="font-weight: 600; color: #16a34a;">${leader['1-3']} keywords</span>
          </div>
          <div style="display: flex; justify-content: space-between; font-size: 0.75rem; margin-bottom: 4px;">
            <span style="color: #757B89;">Positions 4-7</span>
            <span style="font-weight: 600; color: #2563eb;">${leader['4-7']} keywords</span>
          </div>
          <div style="display: flex; justify-content: space-between; font-size: 0.75rem;">
            <span style="color: #757B89;">Total Keywords</span>
            <span style="font-weight: 600; color: #19233A;">${leader.totalKeywords}</span>
          </div>
        </div>
      `;
      
      // Generate the competitor comparison chart
      this.generateCompetitorChart(stats);
    },
    
    updateCompetitorComparisonChart() {
      // Check if we have the data
      if (!this.competitorAnalysisData || !this.competitorAnalysisData.competitors) {
        return;
      }
      
      // Destroy existing chart if it exists
      if (this.competitorChart) {
        this.competitorChart.destroy();
        this.competitorChart = null;
      }
      
      // Wait for DOM update and chart.js to be loaded
      this.$nextTick(() => {
        setTimeout(() => {
          const canvas = document.getElementById('competitorComparisonChart');
          if (!canvas) {
            console.error('Canvas element not found - will retry');
            // Retry after a longer delay
            setTimeout(() => {
              const retryCanvas = document.getElementById('competitorComparisonChart');
              if (retryCanvas && window.Chart) {
                this.createCompetitorChart(retryCanvas);
              }
            }, 500);
            return;
          }
          
          if (!window.Chart) {
            console.error('Chart.js not loaded - loading now');
            this.loadChartJS();
            // Retry after loading
            setTimeout(() => {
              if (window.Chart) {
                this.createCompetitorChart(canvas);
              }
            }, 1000);
            return;
          }
          
          this.createCompetitorChart(canvas);
        }, 200);
      });
    },
    
    createCompetitorChart(canvas) {
      if (!canvas || !window.Chart) return;
      
      const ctx = canvas.getContext('2d');
      
      // Use the fetched competitor analysis data
      const competitorData = this.competitorAnalysisData.competitors || [];
      const businessName = this.scanData.businessName || 'Your Business';
      
      // Helper function to truncate long names
      const truncateName = (name, maxLength = 20) => {
        if (name && name.length > maxLength) {
          return name.substring(0, maxLength - 2) + '..';
        }
        return name;
      };
      
      // Prepare data for chart
      const chartLabels = [];
      const fullNames = [];
      const chartValues = [];
      const chartColors = [];
      
      // First add main business
      const mainBusiness = competitorData.find(b => b.isYourBusiness);
      if (mainBusiness) {
        // Calculate average rank for main business
        let totalRank = 0;
        let rankedCount = 0;
        
        totalRank += (mainBusiness.top3 || 0) * 2;
        rankedCount += (mainBusiness.top3 || 0);
        
        totalRank += (mainBusiness.pos4to7 || 0) * 5.5;
        rankedCount += (mainBusiness.pos4to7 || 0);
        
        totalRank += (mainBusiness.pos8to15 || 0) * 11.5;
        rankedCount += (mainBusiness.pos8to15 || 0);
        
        totalRank += (mainBusiness.pos16to20 || 0) * 18;
        rankedCount += (mainBusiness.pos16to20 || 0);
        
        totalRank += (mainBusiness.notRanked || 0) * 21;
        rankedCount += (mainBusiness.notRanked || 0);
        
        const avgRank = rankedCount > 0 ? (totalRank / rankedCount) : 21;
        
        chartLabels.push(truncateName(mainBusiness.name));
        fullNames.push(mainBusiness.name);
        chartValues.push(avgRank > 20 ? 20 : parseFloat(avgRank.toFixed(1)));
        chartColors.push('#10b981'); // Green for main business
      }
      
      // Then add competitors
      competitorData.forEach(business => {
        if (!business.isYourBusiness) {
          // Calculate average rank
          let totalRank = 0;
          let rankedCount = 0;
          
          totalRank += (business.top3 || 0) * 2;
          rankedCount += (business.top3 || 0);
          
          totalRank += (business.pos4to7 || 0) * 5.5;
          rankedCount += (business.pos4to7 || 0);
          
          totalRank += (business.pos8to15 || 0) * 11.5;
          rankedCount += (business.pos8to15 || 0);
          
          totalRank += (business.pos16to20 || 0) * 18;
          rankedCount += (business.pos16to20 || 0);
          
          totalRank += (business.notRanked || 0) * 21;
          rankedCount += (business.notRanked || 0);
          
          const avgRank = rankedCount > 0 ? (totalRank / rankedCount) : 21;
          
          chartLabels.push(truncateName(business.name));
          fullNames.push(business.name);
          chartValues.push(avgRank > 20 ? 20 : parseFloat(avgRank.toFixed(1)));
          chartColors.push('#E5E7EB'); // Gray for competitors
        }
      });
      
      // Destroy existing chart if it exists
      if (this.competitorChart) {
        this.competitorChart.destroy();
      }
      
      // Create the chart matching EXACT original design
      this.competitorChart = new window.Chart(ctx, {
        type: 'bar',
        data: {
          labels: chartLabels,
          datasets: [{
            label: 'Average Position',
            data: chartValues,
            backgroundColor: chartColors,
            borderColor: chartColors.map(c => c === '#10b981' ? '#059669' : '#D1D5DB'),
            borderWidth: 1,
            borderRadius: {
              topRight: 6,
              bottomRight: 6,
              topLeft: 0,
              bottomLeft: 0
            },
            borderSkipped: false,
            barThickness: 32,
            maxBarThickness: 40
          }]
        },
        options: {
          indexAxis: 'y', // Horizontal bars - THIS IS IMPORTANT!
          responsive: true,
          maintainAspectRatio: false,
          layout: {
            padding: {
              top: 10,
              right: 20,
              bottom: 10,
              left: 10
            }
          },
          scales: {
            x: {
              beginAtZero: true,
              max: Math.max(...chartValues.filter(v => v <= 20)) + 2, // Dynamic max
              border: {
                display: false
              },
              ticks: { 
                color: '#757B89',
                font: {
                  size: 11,
                  family: 'Rubik',
                  weight: '400'
                },
                padding: 8,
                stepSize: 1,
                callback: function(value) {
                  return '#' + value; // Add # prefix
                }
              },
              grid: { 
                color: '#F3F4F6',
                lineWidth: 1,
                drawBorder: false
              }
            },
            y: {
              border: {
                display: false
              },
              ticks: { 
                color: '#374151',
                font: {
                  size: 12,
                  family: 'Rubik',
                  weight: '500'
                },
                padding: 8,
                autoSkip: false,
                mirror: false,
                crossAlign: 'far'
              },
              grid: { 
                display: false,
                drawBorder: false
              }
            }
          },
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              titleColor: '#ffffff',
              bodyColor: '#ffffff',
              borderColor: 'rgba(255, 255, 255, 0.1)',
              borderWidth: 1,
              cornerRadius: 8,
              padding: 12,
              titleFont: {
                size: 13,
                family: 'Rubik',
                weight: '600'
              },
              bodyFont: {
                size: 12,
                family: 'Rubik'
              },
              callbacks: {
                title: function(context) {
                  // Show full name in tooltip
                  return fullNames[context[0].dataIndex] || context[0].label;
                },
                label: function(context) {
                  const actualValue = chartValues[context.dataIndex];
                  if (actualValue >= 21) {
                    return 'Average Position: #20+';
                  }
                  return `Average Position: #${actualValue.toFixed(1)}`;
                }
              },
              displayColors: false
            }
          },
          elements: {
            bar: {
              borderWidth: 1
            }
          },
          animation: {
            duration: 800,
            easing: 'easeInOutQuart'
          }
        }
      });
      
    },
    
    generateSimpleComparisonChart() {
      
      // Wait for Chart.js to load
      setTimeout(() => {
        const canvas = document.getElementById('competitorComparisonChart');
        if (!canvas) {
          console.error('Canvas element not found');
          return;
        }
        
        if (!window.Chart) {
          console.error('Chart.js not loaded');
          return;
        }
        
        const ctx = canvas.getContext('2d');
        
        // Calculate average rankings directly here
        const processItems = (this.scanData.execution && this.scanData.execution.processItems) || [];
        const competitors = this.scanData.competitors || [];
        const businessName = this.scanData.businessName || 'Your Business';
        
        // Calculate main business average
        let mainBusinessTotal = 0;
        let mainBusinessCount = 0;
        let mainBusinessNotRanked = 0;
        
        processItems.forEach(item => {
          const rank = parseInt(item.rank || 0);
          if (rank > 0 && rank <= 20) {
            mainBusinessTotal += rank;
            mainBusinessCount++;
          } else if (rank === 0) {
            mainBusinessNotRanked++;
          }
        });
        
        // Calculate average for main business (treat not ranked as 21)
        let mainBusinessAvg;
        if (mainBusinessCount === 0 && mainBusinessNotRanked > 0) {
          mainBusinessAvg = 21; // All not ranked
        } else if (mainBusinessCount > 0) {
          // Mix of ranked and not ranked - include not ranked as 21 in average
          const totalCount = mainBusinessCount + mainBusinessNotRanked;
          const totalSum = mainBusinessTotal + (mainBusinessNotRanked * 21);
          mainBusinessAvg = parseFloat((totalSum / totalCount).toFixed(1));
        } else {
          mainBusinessAvg = 21; // Default if no data
        }
        
        // Calculate competitor averages
        const competitorData = {};
        
        // Initialize all competitors with no data (will show as 20+)
        competitors.forEach(comp => {
          competitorData[comp.place_id] = {
            name: comp.name,
            total: 0,
            count: 0,
            notFoundCount: processItems.length // Start assuming not found in any
          };
        });
        
        // Process each item to find competitor rankings
        processItems.forEach(item => {
          // Track which competitors were found in this item
          const foundInThisItem = new Set();
          
          if (item.result) {
            try {
              const results = JSON.parse(item.result);
              if (Array.isArray(results)) {
                results.forEach((business, index) => {
                  const rank = index + 1;
                  
                  // Check each competitor
                  competitors.forEach(comp => {
                    if (business.place_id === comp.place_id) {
                      competitorData[comp.place_id].total += rank;
                      competitorData[comp.place_id].count++;
                      competitorData[comp.place_id].notFoundCount--;
                      foundInThisItem.add(comp.place_id);
                    }
                  });
                });
              }
            } catch (e) {
              // Error parsing result
            }
          }
        });
        
        // Helper function to truncate long names
        const truncateName = (name, maxLength = 20) => {
          if (name && name.length > maxLength) {
            return name.substring(0, maxLength - 2) + '..';
          }
          return name;
        };
        
        // Prepare chart data - ALWAYS include all businesses
        const chartLabels = [];
        const chartValues = [];
        const chartColors = [];
        const fullNames = []; // Store full names for tooltip
        
        // Always add main business
        chartLabels.push(truncateName(businessName));
        fullNames.push(businessName);
        chartValues.push(mainBusinessAvg > 20 ? 20 : mainBusinessAvg); // Cap at 20 for display
        chartColors.push('#10b981'); // Green for your business
        
        // Add ALL competitors (even if not found)
        competitors.forEach(comp => {
          const data = competitorData[comp.place_id];
          let avg;
          
          if (data.count === 0) {
            // Never found - show as 20+
            avg = 21;
          } else {
            // Mix of found and not found - include not found as 21 in average
            const totalCount = data.count + data.notFoundCount;
            const totalSum = data.total + (data.notFoundCount * 21);
            avg = totalSum / totalCount;
          }
          
          chartLabels.push(truncateName(comp.name));
          fullNames.push(comp.name); // Store full name
          chartValues.push(avg > 20 ? 20 : parseFloat(avg.toFixed(1))); // Cap at 20 for display
          chartColors.push('#E5E7EB'); // Gray for competitors
        });
        
        // Destroy existing chart if it exists
        if (this.competitorChart) {
          this.competitorChart.destroy();
        }
        
        // Create the chart matching EXACT HTML design
        this.competitorChart = new window.Chart(ctx, {
          type: 'bar',
          data: {
            labels: chartLabels,
            datasets: [{
              label: 'Average Position',
              data: chartValues,
              backgroundColor: chartColors,
              borderColor: chartColors.map(c => c === '#10b981' ? '#059669' : '#D1D5DB'),
              borderWidth: 1,
              borderRadius: {
                topRight: 6,
                bottomRight: 6,
                topLeft: 0,
                bottomLeft: 0
              },
              borderSkipped: false,
              barThickness: 32,
              maxBarThickness: 40
            }]
          },
          options: {
            indexAxis: 'y', // Horizontal bars
            responsive: true,
            maintainAspectRatio: false,
            layout: {
              padding: {
                top: 10,
                right: 20,
                bottom: 10,
                left: 10  // Back to original padding
              }
            },
            scales: {
              x: {
                beginAtZero: true,
                max: Math.max(...chartValues.filter(v => v <= 20)) + 2, // Dynamic max like HTML
                border: {
                  display: false
                },
                ticks: { 
                  color: '#757B89',
                  font: {
                    size: 11,
                    family: 'Rubik',
                    weight: '400'
                  },
                  padding: 8,
                  stepSize: 1,
                  callback: function(value) {
                    return '#' + value; // Add # prefix like HTML
                  }
                },
                grid: { 
                  color: '#F3F4F6',
                  lineWidth: 1,
                  drawBorder: false
                }
              },
              y: {
                border: {
                  display: false
                },
                ticks: { 
                  color: '#374151',
                  font: {
                    size: 12,  // Slightly smaller to fit better
                    family: 'Rubik',
                    weight: '500'
                  },
                  padding: 8,  // Reduced padding
                  autoSkip: false, // Don't skip any labels
                  mirror: false,
                  crossAlign: 'far' // Align labels properly
                },
                grid: { 
                  display: false,
                  drawBorder: false
                }
              }
            },
            plugins: {
              legend: {
                display: false
              },
              tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                titleColor: '#ffffff',
                bodyColor: '#ffffff',
                borderColor: 'rgba(255, 255, 255, 0.1)',
                borderWidth: 1,
                cornerRadius: 8,
                padding: 12,
                titleFont: {
                  size: 13,
                  family: 'Rubik',
                  weight: '600'
                },
                bodyFont: {
                  size: 12,
                  family: 'Rubik'
                },
                callbacks: {
                  title: function(context) {
                    // Show full name in tooltip
                    return fullNames[context[0].dataIndex] || context[0].label;
                  },
                  label: function(context) {
                    const actualValue = chartValues[context.dataIndex];
                    if (actualValue >= 21) {
                      return 'Average Position: #20+';
                    }
                    return `Average Position: #${actualValue.toFixed(1)}`;
                  }
                },
                displayColors: false
              }
            },
            elements: {
              bar: {
                borderWidth: 1
              }
            },
            animation: {
              duration: 800,
              easing: 'easeInOutQuart'
            }
          }
        });
        
      }, 1000);
    },
    
    generateCompetitorChart() {
      // Wait for Chart.js to load
      setTimeout(() => {
        const ctx = document.getElementById('competitorComparisonChart');
        if (!ctx || !window.Chart) return;
        
        // Get the dynamic chart data from computed property
        const chartData = this.competitorComparisonData;
        
        if (chartData.labels.length === 0) {
          // No data to display - show message on canvas
          const context = ctx.getContext('2d');
          context.font = '14px Arial';
          context.fillStyle = '#9ca3af';
          context.textAlign = 'center';
          context.fillText('No ranking data available', ctx.width / 2, ctx.height / 2);
          return;
        }
        
        // Destroy existing chart if it exists
        if (this.competitorChart) {
          this.competitorChart.destroy();
        }
        
        // Create horizontal bar chart with dynamic data
        this.competitorChart = new window.Chart(ctx, {
          type: 'bar',
          data: {
            labels: chartData.labels,
            datasets: [{
              label: 'Average Ranking Position',
              data: chartData.datasets[0].data,
              backgroundColor: chartData.datasets[0].backgroundColor,
              borderColor: chartData.datasets[0].borderColor,
              borderWidth: 2,
              borderRadius: {
                topRight: 6,
                bottomRight: 6,
                topLeft: 0,
                bottomLeft: 0
              },
              borderSkipped: false,
              barThickness: 32,
              maxBarThickness: 40
            }]
          },
          options: {
            indexAxis: 'y', // Horizontal bars
            responsive: true,
            maintainAspectRatio: false,
            layout: {
              padding: {
                top: 10,
                right: 20,
                bottom: 10,
                left: 10
              }
            },
            scales: {
              x: {
                beginAtZero: true,
                max: Math.max(...competitors.map(c => parseFloat(c.avgPosition))) + 2,
                border: {
                  display: false
                },
                ticks: { 
                  color: '#757B89',
                  font: {
                    size: 11,
                    family: 'Rubik',
                    weight: '400'
                  },
                  padding: 8,
                  stepSize: 1,
                  callback: function(value) {
                    return '#' + value;
                  }
                },
                grid: { 
                  color: '#F3F4F6',
                  lineWidth: 1,
                  drawBorder: false
                }
              },
              y: {
                border: {
                  display: false
                },
                ticks: { 
                  color: '#374151',
                  font: {
                    size: 13,
                    family: 'Rubik',
                    weight: '500'
                  },
                  padding: 12,
                  callback: function(value, index) {
                    const competitor = competitors[index];
                    return competitor ? competitor.name : '';
                  }
                },
                grid: { 
                  display: false,
                  drawBorder: false
                }
              }
            },
            plugins: {
              legend: {
                display: false // Hide legend for cleaner look
              },
              tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                titleColor: '#ffffff',
                bodyColor: '#ffffff',
                borderColor: 'rgba(255, 255, 255, 0.1)',
                borderWidth: 1,
                padding: 12,
                cornerRadius: 8,
                displayColors: false,
                callbacks: {
                  label: function(context) {
                    return 'Average Position: #' + context.parsed.x.toFixed(1);
                  }
                }
              }
            }
          }
        });
      }, 1000);
    },
    
    
    handleSortChange() {
      const sortBy = this.sortBy;
      this.keywordData.sort((a, b) => {
        switch(sortBy) {
          case 'position':
            return a.position - b.position;
          case 'change':
            return parseInt(b.change) - parseInt(a.change);
          case 'keyword':
            return a.name.localeCompare(b.name);
          default:
            return 0;
        }
      });
      // Vue template handles keyword cards now
    },
    
    focusOnMetric(metric) {
      // Track metric card click
      this.$posthog.capture('metric_card_clicked', {
        scan_id: this.gridId,
        metric_type: metric,
        current_value: this.getMetricValue(metric)
      });
      
      switch(metric) {
        case 'keywords':
          document.querySelector('.section:last-of-type').scrollIntoView({ behavior: 'smooth' });
          break;
        case 'locations':
          document.querySelector('.section:first-of-type').scrollIntoView({ behavior: 'smooth' });
          break;
        default:
          document.querySelector('.section:last-of-type').scrollIntoView({ behavior: 'smooth' });
      }
    },
    
    getMetricValue(metric) {
      switch(metric) {
        case 'keywords':
          return this.stats.totalKeywords || 0;
        case 'locations':
          return this.stats.totalLocations || 0;
        case 'position':
          return this.stats.avgPosition || 0;
        case 'visibility':
          return (this.stats.visibility && this.stats.visibility.score) || 0;
        default:
          return 0;
      }
    },
    
    closeModal(modalName) {
      switch(modalName) {
        case 'export':
          this.showExportModal = false;
          break;
        case 'schedule':
          this.showScheduleModal = false;
          break;
        case 'share':
          this.showShareModal = false;
          break;
        case 'competitors':
          this.showCompetitorsModal = false;
          this.competitorChangeMessage = null;
          // Clear the autocomplete input
          if (this.$refs.competitorAutocompleteInput) {
            this.$refs.competitorAutocompleteInput.value = '';
          }
          break;
      }
    },
    
    getDefaultSeoLeader() {
      // Return default data when no execution data is available
      return {
        name: this.scanData.businessName || 'Your Business',
        isYourBusiness: true,
        topPositions: 0,
        firstPositions: 0,
        keywordsCount: 0,
        avgPosition: 'N/A',
        marketShare: 0,
        leadMargin: 0,
        momentum: {
          icon: "⏳",
          text: "Pending",
          color: "#6b7280",
          description: "Awaiting data"
        },
        gapToClose: 0
      };
    },
    
    showAddCompetitorModal() {
      // This method would open a modal to add competitors
      // For now, we'll show a message
      this.$swal({
        title: 'Add Competitors',
        text: 'This feature allows you to add competitors to track. Would you like to add competitors now?',
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'Yes, Add Competitors',
        cancelButtonText: 'Maybe Later'
      }).then((result) => {
        if (result.isConfirmed) {
          this.showCompetitorsModal = true;
        }
      });
    },
    
    async processExport() {
      
      // Track export event
      this.$posthog.capture('scan_data_exported', {
        scan_id: this.gridId,
        export_format: this.exportFormat,
        data_type: 'full_report',
        total_keywords: this.stats.totalKeywords || 0,
        total_locations: this.stats.totalLocations || 0
      });
      
      // Close the modal first
      this.closeModal('export');
      
      // Handle different export formats
      if (this.exportFormat === 'png') {
        // Export as PNG image
        await this.exportToImage();
      } else if (this.exportFormat === 'csv') {
        // Export as CSV
        await this.exportToCSV();
      } else if (this.exportFormat === 'pdf') {
        // Export as PDF
        await this.exportToPDF();
      }
    },
    
    // Keyword Export Methods
    openKeywordExportModal(index, keyword) {
      // Ensure we have valid keyword data
      if (!keyword || !keyword.name) {
        this.$swal({
          icon: 'error',
          title: 'Error',
          text: 'Unable to export - keyword data not available'
        });
        return;
      }
      
      this.selectedKeywordIndex = index;
      this.selectedKeywordForExport = keyword;
      // Get the current date range from the dropdown for this keyword
      const selectElement = document.querySelectorAll('.keyword-period-select')[index];
      if (selectElement) {
        this.selectedKeywordDateRange = selectElement.value;
      }
      this.showKeywordExportModal = true;
    },
    
    closeKeywordExportModal() {
      this.showKeywordExportModal = false;
      this.selectedKeywordForExport = null;
      this.selectedKeywordIndex = null;
    },
    
    getDateRangeText(range) {
      const rangeMap = {
        '7': 'Last 7 Days',
        '30': 'Last 30 Days',
        '90': 'Last 90 Days',
        '180': 'Last 6 Months'
      };
      return rangeMap[range] || 'Last 30 Days';
    },
    
    async processKeywordExport() {
      // Track keyword export event
      this.$posthog.capture('scan_data_exported', {
        scan_id: this.gridId,
        export_format: this.keywordExportFormat,
        data_type: 'keyword_report',
        keyword: this.selectedKeywordForExport ? this.selectedKeywordForExport.name : 'unknown'
      });
      
      // Close the modal first
      
      if (this.keywordExportFormat === 'csv') {
        await this.exportKeywordToCSV();
      } else if (this.keywordExportFormat === 'pdf') {
        await this.exportKeywordToPDF();
      }
      this.closeKeywordExportModal();
    },
    
    async exportKeywordToCSV() {

      try {
        // Validate keyword data
        if (!this.selectedKeywordForExport || !this.selectedKeywordForExport.name) {
          throw new Error('Invalid keyword data');
        }
        
        // Show loading state
        this.$swal({
          title: 'Generating Keyword CSV',
          text: `Preparing data for "${this.selectedKeywordForExport.name}"...`,
          allowOutsideClick: false,
          showConfirmButton: false,
          willOpen: () => {
            this.$swal.showLoading();
          }
        });

        // Get keyword detailed data
        const keywordData = this.keywordDetailedData[this.selectedKeywordIndex];
        if (!keywordData || !keywordData.processItems) {
          throw new Error('No data available for this keyword');
        }

        // Prepare CSV data
        const csvData = [];
        
        // Headers (removed Distance column)
        const headers = [
          'Date',
          'Grid Point',
          'Rank',
          'Latitude',
          'Longitude',
          'Competitor Name',
          'Competitor Rank'
        ];
        csvData.push(headers);

        // Process items for the keyword
        keywordData.processItems.forEach((item, index) => {
          const rank = item.rank === 0 ? '20+' : (item.rank || 'Not Ranked');
          
          // Base row data (removed distance calculation)
          const baseRow = [
            new Date(item.scan_date || item.createdAt).toLocaleDateString(),
            `Point ${item.grid_index || index + 1}`,
            rank,
            item.latitude || '',
            item.longitude || ''
          ];
          
          // Add competitor data if available
          if (keywordData.competitors && keywordData.competitors.length > 0) {
            keywordData.competitors.forEach(comp => {
              csvData.push([
                ...baseRow,
                comp.name || '',
                comp.rank || ''
              ]);
            });
          } else {
            csvData.push([...baseRow, '', '']);
          }
        });

        // Add summary statistics
        csvData.push([]); // Empty row
        csvData.push(['Keyword Summary']);
        csvData.push(['Keyword', this.selectedKeywordForExport.name]);
        csvData.push(['Date Range', this.getDateRangeText(this.selectedKeywordDateRange)]);
        csvData.push(['Average Position', keywordData.avgPosition || 'N/A']);
        csvData.push(['Best Position', keywordData.bestPosition || 'N/A']);
        csvData.push(['Worst Position', keywordData.worstPosition || 'N/A']);
        csvData.push(['Top 3 Count', keywordData.processItems.filter(item => item.rank > 0 && item.rank <= 3).length]);
        csvData.push(['Grid Coverage', `${keywordData.gridCoverage || 0}%`]);

        // Convert to CSV string
        const csvContent = csvData.map(row => 
          row.map(cell => {
            const cellStr = String(cell);
            if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
              return `"${cellStr.replace(/"/g, '""')}"`;
            }
            return cellStr;
          }).join(',')
        ).join('\n');

        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `keyword_${this.selectedKeywordForExport.name.replace(/[^a-z0-9]/gi, '_')}_${Date.now()}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.$swal.close();
        
        // Show success message
        this.$swal({
          icon: 'success',
          title: 'CSV Downloaded!',
          text: `Keyword data for "${this.selectedKeywordForExport.name}" has been exported successfully.`,
          timer: 3000,
          showConfirmButton: false
        });

      } catch (error) {
        console.error('Export error:', error);
        this.$swal({
          icon: 'error',
          title: 'Export Failed',
          text: 'There was an error generating the CSV file. Please try again.'
        });
      }
    },
    
    async exportKeywordToPDF() {
      try {
        // Validate keyword data
        if (!this.selectedKeywordForExport || !this.selectedKeywordForExport.name) {
          throw new Error('Invalid keyword data');
        }
        
        // Show loading state
        this.$swal({
          title: 'Generating Keyword PDF',
          text: `Creating report for "${this.selectedKeywordForExport.name}"...`,
          allowOutsideClick: false,
          showConfirmButton: false,
          willOpen: () => {
            this.$swal.showLoading();
          }
        });

        // Get keyword detailed data
        const keywordData = this.keywordDetailedData[this.selectedKeywordIndex];
        if (!keywordData || !keywordData.processItems) {
          throw new Error('No data available for this keyword');
        }
        
        // Store the current active tab
        const originalTab = this.getActiveTab(this.selectedKeywordIndex);
        
        // Switch to heatmap tab to ensure it's rendered
        await this.switchKeywordTab(this.selectedKeywordIndex, 'heatmap');
        
        // Initialize the map if it hasn't been initialized yet
        if (!this.keywordMaps || !this.keywordMaps[this.selectedKeywordIndex]) {
          // The switchKeywordTab should trigger map initialization
          // but we'll make sure by calling initKeywordHeatmap if available
          if (this.initKeywordHeatmap) {
            await this.initKeywordHeatmap(this.selectedKeywordIndex);
          }
        }
        
        // Wait for the heatmap to render and load completely
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // Get the map element for the expanded keyword (refs return arrays)
        const mapRef = this.$refs[`keywordMap${this.selectedKeywordIndex}`];
        const mapElement = mapRef && mapRef[0] ? mapRef[0] : null;
        
        // Import and use the keyword-specific PDF generator
        const { generateKeywordPDF } = await import('@/utils/keywordPdfGenerator');
        const pdf = await generateKeywordPDF(
          keywordData,
          this.scanData,
          this.selectedKeywordForExport,
          this.getDateRangeText(this.selectedKeywordDateRange),
          mapElement
        );
        
        // Restore the original tab
        if (originalTab !== 'heatmap') {
          this.switchKeywordTab(this.selectedKeywordIndex, originalTab);
        }
        
        // Download PDF with keyword-specific filename
        const keywordName = this.selectedKeywordForExport.name.replace(/[^a-z0-9]/gi, '_');
        const date = new Date().toISOString().split('T')[0];
        const fileName = `keyword_report_${keywordName}_${date}.pdf`;
        
        pdf.save(fileName);
        
        this.$swal.close();
        
        // Show success message
        this.$swal({
          icon: 'success',
          title: 'PDF Downloaded!',
          text: `Report for "${this.selectedKeywordForExport.name}" has been generated successfully.`,
          timer: 3000,
          showConfirmButton: false
        });
        
      } catch (error) {
        console.error('PDF export error:', error);
        this.$swal({
          icon: 'error',
          title: 'Export Failed',
          text: 'There was an error generating the PDF report. Please try again.'
        });
      }
    },
    
    async exportToCSV() {
      try {
        // Show loading state
        this.$swal({
          title: 'Generating CSV',
          text: 'Please wait while we prepare your data...',
          allowOutsideClick: false,
          showConfirmButton: false,
          willOpen: () => {
            this.$swal.showLoading();
          }
        });

        // Prepare CSV data
        const csvData = [];
        
        // Headers - Added Keywords column
        const headers = [
          'Grid Point',
          'Keyword',
          'Rank',
          'Latitude', 
          'Longitude',
          'Business Name',
          'Business Address',
          'Status',
          'Distance from Business',
          'Scan Date',
          'Execution ID'
        ];
        csvData.push(headers);

        // Data rows
        this.processItems.forEach((item, index) => {
          const rank = item.rank === 0 ? '20+' : (item.rank || 'Not Ranked');
          const row = [
            `Point ${item.grid_index || index + 1}`,
            item.keyword || '',
            rank,
            item.latitude || '',
            item.longitude || '',
            this.scanData.businessName || '',
            this.scanData.address || '',
            item.status || '',
            this.calculateDistance(
              Number(this.scanData.latitude || 0),
              Number(this.scanData.longitude || 0),
              Number(item.latitude),
              Number(item.longitude)
            ).toFixed(2) + ' km',
            new Date(item.scan_date || item.createdAt || Date.now()).toLocaleDateString(),
            this.executionId || ''
          ];
          csvData.push(row);
        });

        // Add summary statistics at the end
        csvData.push([]); // Empty row
        csvData.push(['Summary Statistics']);
        csvData.push(['Total Grid Points', this.processItems.length]);
        csvData.push(['Keywords', [...new Set(this.processItems.map(item => item.keyword))].join(', ')]);
        csvData.push(['Average Rank', this.calculateAverageRank()]);
        csvData.push(['Top 3 Count', this.processItems.filter(item => item.rank > 0 && item.rank <= 3).length]);
        csvData.push(['Top 10 Count', this.processItems.filter(item => item.rank > 0 && item.rank <= 10).length]);
        csvData.push(['Not Ranked Count', this.processItems.filter(item => !item.rank || item.rank === 0).length]);
        csvData.push(['Grid Size', this.scanData.gridSize || 'N/A']);
        csvData.push(['Scan Type', 'Grid Scan']);

        // Convert to CSV string
        const csvContent = csvData.map(row => 
          row.map(cell => {
            // Escape quotes and wrap in quotes if contains comma
            const cellStr = String(cell);
            if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
              return `"${cellStr.replace(/"/g, '""')}"`;
            }
            return cellStr;
          }).join(',')
        ).join('\n');

        // Download CSV
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        const businessName = this.scanData.businessName 
          ? this.scanData.businessName.replace(/[^a-z0-9]/gi, '_') 
          : 'grid_scan';
        const date = new Date().toISOString().split('T')[0];
        link.setAttribute('download', `grid_scan_${businessName}_${date}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Cleanup
        URL.revokeObjectURL(url);
        
        // Show success message
        this.$swal({
          icon: 'success',
          title: 'CSV Downloaded!',
          text: `Grid scan data exported successfully`,
          timer: 2000,
          showConfirmButton: false
        });
        
      } catch (error) {
        console.error('Error exporting CSV:', error);
        this.$swal({
          icon: 'error',
          title: 'Export Failed',
          text: 'Failed to export CSV data. Please try again.',
          confirmButtonText: 'OK'
        });
      }
    },
    
    calculateDistance(lat1, lon1, lat2, lon2) {
      const R = 6371; // Radius of the Earth in km
      const dLat = this.deg2rad(lat2 - lat1);
      const dLon = this.deg2rad(lon2 - lon1);
      const a = 
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) * 
        Math.sin(dLon/2) * Math.sin(dLon/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      const d = R * c; // Distance in km
      return d;
    },

    deg2rad(deg) {
      return deg * (Math.PI/180);
    },
    
    calculateAverageRank() {
      const rankedItems = this.processItems.filter(item => item.rank && item.rank > 0);
      if (rankedItems.length === 0) return 'N/A';
      const sum = rankedItems.reduce((acc, item) => acc + item.rank, 0);
      return (sum / rankedItems.length).toFixed(1);
    },
    
    async exportToPDF() {
      try {
        // Show loading state
        this.$swal({
          title: 'Generating PDF',
          text: 'Please wait while we prepare your report...',
          allowOutsideClick: false,
          showConfirmButton: false,
          willOpen: () => {
            this.$swal.showLoading();
          }
        });
        
        // First, capture the map section as an image
        let mapImageData = null;
        try {
          // Create a temporary container for capturing the map section
          const captureContainer = document.createElement('div');
          captureContainer.style.position = 'absolute';
          captureContainer.style.left = '-9999px';
          captureContainer.style.top = '0';
          captureContainer.style.width = '1200px';
          captureContainer.style.backgroundColor = '#ffffff';
          captureContainer.style.padding = '20px';
          
          // Get the geographic performance section
          const geoSection = document.querySelector('.geographic-performance-section');
          
          if (geoSection) {
            // Clone the entire section
            const sectionClone = geoSection.cloneNode(true);
            
            // Remove any buttons from the clone
            const buttons = sectionClone.querySelectorAll('button');
            buttons.forEach(btn => btn.remove());
            
            // Ensure the map container maintains its dimensions
            const mapContainer = sectionClone.querySelector('.google-maps-container');
            if (mapContainer) {
              mapContainer.style.height = '500px';
              mapContainer.style.width = '100%';
            }
            
            // Append to capture container
            captureContainer.appendChild(sectionClone);
            document.body.appendChild(captureContainer);
            
            // Wait for DOM to update
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Use html2canvas to capture
            const html2canvas = (await import('html2canvas')).default;
            const canvas = await html2canvas(captureContainer, {
              useCORS: true,
              allowTaint: true,
              logging: false,
              scale: 2, // Higher quality
              backgroundColor: '#ffffff',
              windowWidth: 1200,
              windowHeight: captureContainer.scrollHeight
            });
            
            // Convert to data URL
            mapImageData = canvas.toDataURL('image/png');
            
            // Remove temporary container
            document.body.removeChild(captureContainer);
          }
        } catch (error) {
          console.log('Could not capture map for PDF, continuing without it');
        }
        
        // Prepare scan data for PDF
        const pdfScanData = {
          businessName: this.scanData.businessName,
          address: this.scanData.address,
          gridSize: this.scanData.gridSize || '5×5',
          radius: this.scanData.radius,
          distance_type: this.scanData.distance_type,
          keywords: this.processItems.length > 0 
            ? [...new Set(this.processItems.map(item => item.keyword))]
            : []
        };
        
        // Import and generate PDF from dedicated Grid Scan PDF generator
        const { generateGridScanPDF } = await import('@/utils/gridScanPdfGenerator');
        const pdf = await generateGridScanPDF(pdfScanData, this.processItems, mapImageData);
        
        // Download PDF
        const businessName = this.scanData.businessName 
          ? this.scanData.businessName.replace(/[^a-z0-9]/gi, '_') 
          : 'grid_scan';
        const date = new Date().toISOString().split('T')[0];
        const fileName = `grid_scan_report_${businessName}_${date}.pdf`;
        
        pdf.save(fileName);
        
        // Show success message
        this.$swal({
          icon: 'success',
          title: 'PDF Generated!',
          text: `Report saved as ${fileName}`,
          timer: 2000,
          showConfirmButton: false
        });
        
      } catch (error) {
        console.error('Error generating PDF:', error);
        this.$swal({
          icon: 'error',
          title: 'PDF Generation Failed',
          text: 'Failed to generate PDF report. Please try again.',
          confirmButtonText: 'OK'
        });
      }
    },
    
    async exportToImage() {
      try {
        // Show loading state
        this.$swal({
          title: 'Generating Image',
          text: 'Please wait while we capture the geographic performance section...',
          allowOutsideClick: false,
          showConfirmButton: false,
          willOpen: () => {
            this.$swal.showLoading();
          }
        });

        // Wait for modal to close and any pending updates
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Create a temporary container for capturing
        const captureContainer = document.createElement('div');
        captureContainer.style.position = 'absolute';
        captureContainer.style.left = '-9999px';
        captureContainer.style.top = '0';
        captureContainer.style.width = '1200px';
        captureContainer.style.backgroundColor = '#ffffff';
        captureContainer.style.padding = '20px';
        
        // Get the geographic performance section
        const geoSection = document.querySelector('.geographic-performance-section');
        
        if (!geoSection) {
          throw new Error('Geographic performance section not found');
        }
        
        // Clone the entire section
        const sectionClone = geoSection.cloneNode(true);
        
        // Remove the download image button from the clone if it exists
        const downloadBtn = sectionClone.querySelector('.export-map-btn');
        if (downloadBtn) {
          downloadBtn.remove();
        }
        
        // Ensure the map container maintains its dimensions
        const mapContainer = sectionClone.querySelector('.google-maps-container');
        if (mapContainer) {
          mapContainer.style.height = '500px';
          mapContainer.style.width = '100%';
        }
        
        // Append to capture container
        captureContainer.appendChild(sectionClone);
        document.body.appendChild(captureContainer);
        
        // Wait for DOM to update
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Use html2canvas to capture
        const html2canvas = (await import('html2canvas')).default;
        const canvas = await html2canvas(captureContainer, {
          useCORS: true,
          allowTaint: true,
          logging: false,
          scale: 2, // Higher quality
          backgroundColor: '#ffffff',
          windowWidth: 1200,
          windowHeight: captureContainer.scrollHeight
        });
        
        // Remove temporary container
        document.body.removeChild(captureContainer);
        
        // Convert to blob and download
        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          
          // Generate filename
          const businessName = this.scanData.businessName 
            ? this.scanData.businessName.replace(/[^a-z0-9]/gi, '_') 
            : 'grid_scan';
          const date = new Date().toISOString().split('T')[0];
          const fileName = `${businessName}_geographic_performance_${date}.png`;
          
          link.href = url;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          // Cleanup
          URL.revokeObjectURL(url);
          
          // Close loading and show success
          this.$swal({
            icon: 'success',
            title: 'Image Downloaded!',
            text: `Geographic performance map saved as ${fileName}`,
            timer: 2000,
            showConfirmButton: false
          });
        }, 'image/png');
        
      } catch (error) {
        console.error('Error downloading map image:', error);
        this.$swal({
          icon: 'error',
          title: 'Download Failed',
          text: 'Failed to capture the geographic performance section. Please try again.',
          confirmButtonText: 'OK'
        });
      }
    },
    
    setupSchedule() {
      // TODO: Implement actual scheduling functionality
      alert('Reports scheduled successfully!');
      this.closeModal('schedule');
    },
    
    updateShareOptions() {
      // Update share URL based on selected options
      this.shareUrl = `https://gridrank.com/share/${this.generateRandomId()}?type=${this.shareType}&access=${this.accessLevel}&expires=${this.linkExpiration}`;
    },
    
    copyShareLink() {
      navigator.clipboard.writeText(this.shareUrl).then(() => {
        alert('Link copied to clipboard!');
      });
    },
    
    generateNewShareLink() {
      this.shareUrl = `https://gridrank.com/share/${this.generateRandomId()}?type=${this.shareType}&access=${this.accessLevel}&expires=${this.linkExpiration}`;
      alert('New share link generated!');
    },
    
    generateRandomId() {
      return Math.random().toString(36).substr(2, 9) + Math.random().toString(36).substr(2, 9);
    },
    
    initCompetitorAutocomplete() {
      if (!this.$refs.competitorAutocompleteInput) {
        return;
      }
      
      // Check if Google Maps is loaded
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        // If Google Maps not loaded yet, try again after a delay
        setTimeout(() => {
          if (this.showCompetitorsModal) {
            this.initCompetitorAutocomplete();
          }
        }, 1000);
        return;
      }
      
      // Get business location for biasing search results
      const businessData = this.scanData.business || {};
      const lat = businessData.latitude || (businessData.location && businessData.location.lat);
      const lng = businessData.longitude || (businessData.location && businessData.location.lng);
      
      // Create autocomplete with location bias
      const options = {
        types: ['establishment'],
        fields: ['place_id', 'name', 'formatted_address', 'geometry', 'types', 'rating']
      };
      
      // Add location bias if we have coordinates
      if (lat && lng) {
        const center = new window.google.maps.LatLng(lat, lng);
        const circle = new window.google.maps.Circle({
          center: center,
          radius: 5000 // 5km radius
        });
        options.bounds = circle.getBounds();
        options.strictBounds = false; // Allow results outside bounds but prefer within
      }
      
      this.competitorAutocomplete = new window.google.maps.places.Autocomplete(
        this.$refs.competitorAutocompleteInput,
        options
      );
      
      // Handle place selection
      this.competitorAutocomplete.addListener('place_changed', () => {
        const place = this.competitorAutocomplete.getPlace();
        
        if (!place.place_id) {
          this.$swal({
            icon: 'warning',
            title: 'Invalid Selection',
            text: 'Please select a business from the dropdown.',
            confirmButtonColor: '#F59E0B'
          });
          return;
        }
        
        // Check if it's the main business
        const mainBusinessPlaceId = businessData.placeId || businessData.place_id;
        if (place.place_id === mainBusinessPlaceId) {
          this.$swal({
            icon: 'warning',
            title: 'Invalid Selection',
            text: 'You cannot add your own business as a competitor.',
            confirmButtonColor: '#F59E0B'
          });
          this.$refs.competitorAutocompleteInput.value = '';
          return;
        }
        
        // Check if already exists
        const exists = this.currentCompetitors.some(comp => 
          comp.place_id === place.place_id || 
          (comp.business_data && comp.business_data.place_id === place.place_id)
        );
        
        if (exists) {
          this.$swal({
            icon: 'info',
            title: 'Already Added',
            text: 'This competitor is already in your list.',
            confirmButtonColor: '#3B82F6'
          });
          this.$refs.competitorAutocompleteInput.value = '';
          return;
        }
        
        // Add the competitor
        this.addCompetitorFromAutocomplete(place);
      });
    },
    
    async addCompetitorFromAutocomplete(place) {
      try {
        // Prepare competitor data
        const competitorData = {
          place_id: place.place_id,
          name: place.name || 'Unknown Business',
          address: place.formatted_address || '',
          location: place.geometry ? {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng()
          } : null,
          types: place.types || [],
          rating: place.rating || null
        };
        
        const response = await axios.post(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/grid-scan/add-competitor`,
          {
            searchId: this.gridId,
            competitor: competitorData
          }
        );
        
        if (response.data.success) {
          // Add to local list
          this.currentCompetitors.push({
            id: response.data.competitor.id,
            place_id: competitorData.place_id,
            name: competitorData.name,
            business_data: {
              ...competitorData,
              address: competitorData.address
            }
          });
          
          // Clear the input field
          this.$refs.competitorAutocompleteInput.value = '';
          
          // Close modal and show success message
          this.showCompetitorsModal = false;
          
          // Show success message with SweetAlert
          this.$swal({
            icon: 'success',
            title: 'Competitor Added Successfully',
            text: `"${competitorData.name}" has been added as a competitor. Stats will be updated.`,
            confirmButtonText: 'OK',
            confirmButtonColor: '#4F46E5'
          }).then(() => {
            // Reload the page after user clicks OK
            window.location.reload();
          });
        }
      } catch (error) {
        console.error('Error adding competitor:', error);
        const errorMessage = error.response && error.response.data && error.response.data.message;
        
        // Clear the input field
        this.$refs.competitorAutocompleteInput.value = '';
        
        // Show error message with SweetAlert
        this.$swal({
          icon: 'error',
          title: 'Failed to Add Competitor',
          text: errorMessage || 'An error occurred while adding the competitor. Please try again.',
          confirmButtonColor: '#EF4444'
        });
      }
    },
    
    async removeCompetitor(competitorId) {
      try {
        this.isRemovingCompetitor = true;
        const response = await axios.post(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/grid-scan/remove-competitor`,
          {
            searchId: this.gridId,
            competitorId: competitorId
          }
        );
        
        if (response.data.success) {
          // Remove from local list
          this.currentCompetitors = this.currentCompetitors.filter(
            c => c.id !== competitorId
          );
          
          // Get competitor name for the message
          const removedCompetitor = this.currentCompetitors.find(c => c.id === competitorId);
          const competitorName = removedCompetitor ? removedCompetitor.name : 'Competitor';
          
          // Close modal and show success message
          this.showCompetitorsModal = false;
          
          // Show success message with SweetAlert
          this.$swal({
            icon: 'success',
            title: 'Competitor Removed Successfully',
            text: `The competitor has been removed. Stats will be updated.`,
            confirmButtonText: 'OK',
            confirmButtonColor: '#4F46E5'
          }).then(() => {
            // Reload the page after user clicks OK
            window.location.reload();
          });
        }
      } catch (error) {
        console.error('Error removing competitor:', error);
        const errorMessage = error.response && error.response.data && error.response.data.message;
        
        // Show error message with SweetAlert
        this.$swal({
          icon: 'error',
          title: 'Failed to Remove Competitor',
          text: errorMessage || 'An error occurred while removing the competitor. Please try again.',
          confirmButtonColor: '#EF4444'
        });
      } finally {
        this.isRemovingCompetitor = false;
      }
    },
    
    getKeywordCompetitors(items) {
      const competitors = this.scanData.competitors || [];
      const businessName = this.scanData.businessName || 'Your Business';
      
      // Track competitor rankings across all items
      const competitorStats = {};
      
      // Initialize with main business
      competitorStats[businessName] = {
        name: businessName,
        positions: [],
        totalRank: 0,
        count: 0,
        bestPosition: 999
      };
      
      // Initialize competitors
      competitors.forEach(comp => {
        competitorStats[comp.place_id] = {
          name: comp.name,
          positions: [],
          totalRank: 0,
          count: 0,
          bestPosition: 999
        };
      });
      
      // Process each item's result JSON
      items.forEach(item => {
        // Track main business rank
        const mainRank = parseInt(item.rank || 0);
        if (mainRank > 0 && mainRank <= 20) {
          competitorStats[businessName].positions.push(mainRank);
          competitorStats[businessName].totalRank += mainRank;
          competitorStats[businessName].count++;
          if (mainRank < competitorStats[businessName].bestPosition) {
            competitorStats[businessName].bestPosition = mainRank;
          }
        }
        
        // Process competitors from result JSON
        if (item.result) {
          try {
            const results = JSON.parse(item.result);
            if (Array.isArray(results)) {
              results.forEach((business, index) => {
                const rank = index + 1;
                
                competitors.forEach(comp => {
                  if (business.place_id === comp.place_id) {
                    if (rank <= 20) {
                      competitorStats[comp.place_id].positions.push(rank);
                      competitorStats[comp.place_id].totalRank += rank;
                      competitorStats[comp.place_id].count++;
                      if (rank < competitorStats[comp.place_id].bestPosition) {
                        competitorStats[comp.place_id].bestPosition = rank;
                      }
                    }
                  }
                });
              });
            }
          } catch (e) {
            console.log('Error parsing result for competitors:', e);
          }
        }
      });
      
      // Format competitor data for display
      const formattedCompetitors = Object.values(competitorStats)
        .map(comp => {
          const avgPosition = comp.count > 0 
            ? Math.round(comp.totalRank / comp.count)
            : 'NR';
          
          const currentPosition = comp.positions.length > 0 
            ? comp.positions[comp.positions.length - 1]
            : 'NR';
          
          // Calculate trend (simplified)
          let change = '0';
          if (comp.positions.length >= 2) {
            const prevPosition = comp.positions[comp.positions.length - 2];
            const diff = prevPosition - currentPosition;
            if (diff > 0) {
              change = `+${diff}`;
            } else if (diff < 0) {
              change = `${diff}`;
            }
          }
          
          return {
            name: comp.name,
            position: currentPosition,
            change: change,
            duration: `${comp.count} measurements`,
            avgPosition: avgPosition,
            bestPosition: comp.bestPosition === 999 ? 'NR' : comp.bestPosition
          };
        })
        .sort((a, b) => {
          // Sort by current position (NR goes last)
          if (a.position === 'NR' && b.position === 'NR') return 0;
          if (a.position === 'NR') return 1;
          if (b.position === 'NR') return -1;
          return a.position - b.position;
        });
      
      return formattedCompetitors;
    },
    
    switchKeywordTab(index, tabName) {
      // Update the active tab for this keyword
      this.$set(this.keywordActiveTabs, index, tabName);
      
      // Initialize map if heatmap tab
      if (tabName === 'heatmap') {
        // Check if Google Maps is loaded
        if (!window.google || !window.google.maps) {
          this.loadGoogleMaps(() => {
            this.$nextTick(() => {
              this.initKeywordMapAfterLoad(index);
            });
          });
        } else {
          this.$nextTick(() => {
            this.initKeywordMapAfterLoad(index);
          });
        }
      }
    },
    
    initKeywordMapAfterLoad(index) {
      // Ensure unique map container
      if (!this.keywordMaps) {
        this.keywordMaps = {};
      }
      if (!this.keywordOverlays) {
        this.keywordOverlays = {};
      }
      this.initKeywordMap(index);
      this.updateKeywordHeatmap(index, this.keywordSelectedPeriods[index] || 30);
    },
    
    // REMOVED duplicate updateKeywordData - using the async version above
    
    updateCharts(index, period) {
      if (!window.Chart) return;
      
      const keyword = this.keywordData[index];
      const ctx = document.getElementById(`chart-${index}`);
      if (!ctx) return;
      
      // Generate data based on period
      let labels, dataPoints;
      switch(period) {
        case '7':
          // Last 7 days
          labels = [];
          for (let i = 6; i >= 0; i--) {
            const date = new Date();
            date.setDate(date.getDate() - i);
            if (i === 6) {
              labels.push(date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
            } else {
              labels.push(date.getDate().toString());
            }
          }
          dataPoints = 7;
          break;
        case '30':
          // Last 30 days - show every 3rd day
          labels = [];
          for (let i = 29; i >= 0; i -= 3) {
            const date = new Date();
            date.setDate(date.getDate() - i);
            if (i === 29) {
              labels.push(date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
            } else {
              labels.push(date.getDate().toString());
            }
          }
          dataPoints = labels.length;
          break;
        case '90':
          // Last 90 days - weekly intervals
          labels = [];
          for (let i = 12; i >= 0; i--) {
            const date = new Date();
            date.setDate(date.getDate() - (i * 7));
            if (i === 12) {
              labels.push(date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
            } else {
              labels.push(date.getDate().toString());
            }
          }
          dataPoints = 13;
          break;
        case '180':
          // Last 6 months
          labels = [];
          for (let i = 5; i >= 0; i--) {
            const date = new Date();
            date.setMonth(date.getMonth() - i);
            labels.push(date.toLocaleDateString('en-US', { month: 'short' }));
          }
          dataPoints = 6;
          break;
        default:
          labels = ['Week 1', 'Week 2', 'Week 3', 'Week 4'];
          dataPoints = 4;
      }
      
      // Generate datasets for competitors
      const colorMap = [
        '#10b981',  // Your Business gets green
        '#4f46e5',  // Main competitor gets blue
        '#ef4444',  // Secondary competitor gets red
        '#f59e0b',  // Orange
        '#8b5cf6',  // Purple
        '#ec4899'   // Pink
      ];
      
      const datasets = keyword.competitors.map((competitor, i) => {
        const isYourBusiness = competitor.name === 'Your Business';
        const trendData = this.generateTrendData(competitor.position, dataPoints);
        const lineColor = isYourBusiness ? '#10b981' : colorMap[i % colorMap.length];
        
        return {
          label: competitor.name,
          data: trendData,
          borderColor: lineColor,
          backgroundColor: 'transparent',
          borderWidth: isYourBusiness ? 3 : 2.5,
          fill: false,
          tension: 0.3,
          pointBackgroundColor: lineColor,
          pointBorderColor: '#FFFFFF',
          pointBorderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 6,
          pointHoverBackgroundColor: lineColor,
          pointHoverBorderColor: '#FFFFFF',
          pointHoverBorderWidth: 3
        };
      });
      
      // Destroy existing chart if it exists
      if (this.keywordCharts && this.keywordCharts[index]) {
        this.keywordCharts[index].destroy();
      }
      
      if (!this.keywordCharts) {
        this.keywordCharts = {};
      }
      
      // Create new chart
      this.keywordCharts[index] = new window.Chart(ctx, {
        type: 'line',
        data: {
          labels: labels,
          datasets: datasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: 'index',
            intersect: false
          },
          plugins: {
            legend: {
              display: true,
              position: 'bottom',
              labels: {
                usePointStyle: true,
                padding: 15,
                font: {
                  size: 11,
                  family: 'Rubik',
                  weight: '400'
                },
                color: '#374151'
              }
            },
            tooltip: {
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              titleColor: '#ffffff',
              bodyColor: '#ffffff',
              borderColor: 'rgba(255, 255, 255, 0.1)',
              borderWidth: 1,
              padding: 12,
              cornerRadius: 8,
              displayColors: true,
              callbacks: {
                label: function(context) {
                  return context.dataset.label + ': Position #' + context.parsed.y;
                }
              }
            }
          },
          scales: {
            y: {
              reverse: true,
              beginAtZero: false,
              min: 1,
              max: 20,
              border: {
                display: false
              },
              ticks: {
                stepSize: 1,
                color: '#757B89',
                font: {
                  size: 11,
                  family: 'Rubik',
                  weight: '400'
                },
                padding: 8,
                callback: function(value) {
                  return '#' + value;
                }
              },
              grid: {
                color: '#F3F4F6',
                lineWidth: 1,
                drawBorder: false
              }
            },
            x: {
              border: {
                display: false
              },
              ticks: {
                color: '#757B89',
                font: {
                  size: 11,
                  family: 'Rubik',
                  weight: '400'
                },
                padding: 8
              },
              grid: {
                display: false,
                drawBorder: false
              }
            }
          }
        }
      });
    },
    
    initKeywordHeatmap(index) {
      // Wait for Google Maps to be loaded
      if (!window.google || !window.google.maps) {
        setTimeout(() => this.initKeywordHeatmap(index), 500);
        return;
      }
      
      const keyword = this.keywordData[index];
      const mapElement = this.$refs[`keywordMap${index}`];
      
      if (!mapElement || !mapElement[0]) return;
      
      // Initialize map if not already created
      if (!this.keywordMaps[index]) {
        this.keywordMaps[index] = new google.maps.Map(mapElement[0], {
          zoom: 14,
          center: { lat: 28.5887354, lng: 77.0829215 },
          mapTypeId: 'roadmap',
          styles: [
            { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
            { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
            { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] }
          ]
        });
      }
      
      const map = this.keywordMaps[index];
      
      // Clear existing overlays
      if (this.keywordOverlays && this.keywordOverlays[index]) {
        this.keywordOverlays[index].forEach(overlay => overlay.setMap(null));
      }
      
      if (!this.keywordOverlays) {
        this.keywordOverlays = {};
      }
      this.keywordOverlays[index] = [];
      
      // Generate grid points (5x5 grid)
      const gridSize = 5;
      const cellSize = 0.002; // Approximate size for each grid cell
      const center = map.getCenter();
      const centerLat = center.lat();
      const centerLng = center.lng();
      
      // Create colored rectangles for each grid point
      for (let i = 0; i < gridSize; i++) {
        for (let j = 0; j < gridSize; j++) {
          const lat = centerLat - (cellSize * 2) + (i * cellSize);
          const lng = centerLng - (cellSize * 2) + (j * cellSize);
          
          // Generate random rank for demo
          const rank = Math.floor(Math.random() * 20) + 1;
          let fillColor;
          
          if (rank <= 3) fillColor = '#10b981';
          else if (rank <= 7) fillColor = '#3b82f6';
          else if (rank <= 15) fillColor = '#FFB443';
          else fillColor = '#ef4444';
          
          const rectangle = new google.maps.Rectangle({
            bounds: {
              north: lat + cellSize/2,
              south: lat - cellSize/2,
              east: lng + cellSize/2,
              west: lng - cellSize/2
            },
            fillColor: fillColor,
            fillOpacity: 0.6,
            strokeColor: fillColor,
            strokeOpacity: 0.8,
            strokeWeight: 1,
            map: map
          });
          
          this.keywordOverlays[index].push(rectangle);
        }
      }
      
      // Add a marker for the business location
      const marker = new google.maps.Marker({
        position: { lat: centerLat, lng: centerLng },
        map: map,
        title: keyword.name,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: '#4f46e5',
          fillOpacity: 1,
          strokeColor: '#FFFFFF',
          strokeWeight: 2
        }
      });
      
      this.keywordOverlays[index].push(marker);
    },
    
    updateKeywordHeatmap(index, period) {
      // Use backend data if available - prioritize keywordDetailedData which has fresh data
      const keywordData = this.keywordDetailedData[index] || this.dynamicKeywordData[index];
      
      if (!keywordData) {
        return;
      }
      
      if (!window.google || !window.google.maps) {
        return;
      }
      
      // Initialize map if not exists
      if (!this.keywordMaps || !this.keywordMaps[index]) {
        this.initKeywordMap(index);
      }
      
      const map = this.keywordMaps[index];
      if (!map) {
        return;
      }
      
      // Clear existing overlays for this keyword
      if (!this.keywordOverlays) {
        this.keywordOverlays = {};
      }
      if (!this.keywordOverlays[index]) {
        this.keywordOverlays[index] = [];
      }
      
      this.keywordOverlays[index].forEach(overlay => {
        if (overlay && overlay.setMap) overlay.setMap(null);
      });
      this.keywordOverlays[index] = [];
      
      // Use locationData from backend (latest processItems data)
      const locationData = keywordData.locationData || [];
      const bounds = new google.maps.LatLngBounds();
      
      // Display markers with average ranks for the selected period
      if (locationData.length > 0) {
        locationData.forEach(location => {
          // Use the average rank from backend for the selected period
          // The backend now sends average rank for all measurements in the period
          const avgRank = location.rank; // This is already the average
          
          const position = new window.google.maps.LatLng(location.lat, location.lng);
          bounds.extend(position);
          
          // Use same color function as main map with average rank
          const color = this.getRankColor(avgRank);
          
          // Create rank display text showing average
          const rankDisplay = avgRank <= 20 ? avgRank.toFixed(1) : '20+';
          
          // Create marker with same design as main map, showing average for the period
          const marker = new window.google.maps.Marker({
            position: position,
            map: map,
            icon: {
              url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(this.createMarkerSVG(color, rankDisplay))}`,
              scaledSize: new window.google.maps.Size(32, 48),
              anchor: new window.google.maps.Point(16, 48) // Anchor at the bottom point of the pin
            },
            title: `Average Rank: ${rankDisplay} (${location.measurement_count} measurement${location.measurement_count !== 1 ? 's' : ''})`
          });
          
          // Add click handler to show info window
          marker.addListener('click', () => {
            if (this.keywordInfoWindow) {
              this.keywordInfoWindow.close();
            }
            
            const infoContent = `
              <div style="padding: 12px; font-family: 'Rubik', sans-serif;">
                <h4 style="margin: 0 0 8px 0; color: #19233A; font-size: 14px;">
                  ${keywordData.keyword || keywordData.name}
                </h4>
                <div style="color: #757B89; font-size: 12px; line-height: 1.6;">
                  <div><strong>Average Rank:</strong> #${rankDisplay}</div>
                  <div><strong>Measurements:</strong> ${location.measurement_count}</div>
                  <div><strong>Latest Rank:</strong> #${location.latest_rank > 0 && location.latest_rank <= 20 ? location.latest_rank : '20+'}</div>
                  <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #E5E7EB;">
                    <strong>Location:</strong><br>
                    ${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}
                  </div>
                </div>
              </div>
            `;
            
            this.keywordInfoWindow = new window.google.maps.InfoWindow({
              content: infoContent
            });
            
            this.keywordInfoWindow.open(map, marker);
          });
          
          this.keywordOverlays[index].push(marker);
        });
        
        
        // Fit map to show all markers
        if (locationData.length > 0) {
          map.fitBounds(bounds);
          // Add some padding
          setTimeout(() => {
            const currentZoom = map.getZoom();
            if (currentZoom && currentZoom > 15) {
              map.setZoom(15);
            }
          }, 300);
        }
        
        return; // Exit early since we used backend data
      }
      
      // Fallback to local data processing if no backend data
      const filteredItems = this.getFilteredKeywordData(keywordData, period);
      
      // Group by location to calculate average per location
      const locationMap = {};
      
      filteredItems.forEach(item => {
        const key = `${item.latitude},${item.longitude}`;
        if (!locationMap[key]) {
          locationMap[key] = {
            lat: parseFloat(item.latitude),
            lng: parseFloat(item.longitude),
            ranks: [],
            totalRank: 0,
            count: 0,
            bestRank: 999
          };
        }
        
        const rank = parseInt(item.rank || 0);
        if (rank > 0) {
          locationMap[key].ranks.push(rank);
          locationMap[key].totalRank += rank;
          locationMap[key].count++;
          if (rank < locationMap[key].bestRank) {
            locationMap[key].bestRank = rank;
          }
        } else {
          locationMap[key].ranks.push(21); // Not ranked
        }
      });
      
      // Create markers for each location
      
      Object.values(locationMap).forEach(location => {
        const avgRank = location.count > 0 
          ? location.totalRank / location.count 
          : 21;
        
        const position = new window.google.maps.LatLng(location.lat, location.lng);
        bounds.extend(position);
        
        // Determine color based on rank
        let color = '#dc2626'; // Red for not ranked or poor
        if (avgRank <= 3) {
          color = '#10b981'; // Green for top 3
        } else if (avgRank <= 7) {
          color = '#3b82f6'; // Blue for 4-7
        } else if (avgRank <= 15) {
          color = '#f59e0b'; // Orange for 8-15
        }
        
        const marker = new window.google.maps.Marker({
          position: position,
          map: map,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            fillColor: color,
            fillOpacity: 0.8,
            strokeColor: '#ffffff',
            strokeWeight: 2,
            scale: 10
          },
          title: `Avg Rank: ${avgRank <= 20 ? avgRank.toFixed(1) : '20+'}`
        });
        
        // Add info window
        const infoWindow = new window.google.maps.InfoWindow({
          content: `
            <div style="padding: 8px; font-family: 'Rubik', sans-serif;">
              <strong>${keyword.name}</strong><br>
              Average Rank: ${avgRank <= 20 ? '#' + avgRank.toFixed(1) : 'Not Ranked'}<br>
              Measurements: ${location.count}<br>
              Location: ${location.lat.toFixed(4)}, ${location.lng.toFixed(4)}
            </div>
          `
        });
        
        marker.addListener('click', () => {
          infoWindow.open(map, marker);
        });
        
        this.keywordOverlays[index].push(marker);
        this.keywordOverlays[index].push(infoWindow);
      });
      
      // Fit map to show all markers
      if (Object.keys(locationMap).length > 0) {
        map.fitBounds(bounds);
        // Add some padding
        setTimeout(() => {
          const currentZoom = map.getZoom();
          if (currentZoom > 15) {
            map.setZoom(15);
          }
        }, 300);
      }
    },
    
    initKeywordMap(index) {
      // Check if Google Maps is loaded
      if (!window.google || !window.google.maps) {
        console.error('Google Maps not loaded yet');
        return;
      }
      
      const mapElement = this.$refs[`keywordMap${index}`];
      if (!mapElement || !mapElement[0]) {
        return;
      }
      
      // Destroy existing map if it exists
      if (this.keywordMaps && this.keywordMaps[index]) {
        // Clear all overlays first
        if (this.keywordOverlays && this.keywordOverlays[index]) {
          this.keywordOverlays[index].forEach(overlay => {
            if (overlay.setMap) overlay.setMap(null);
          });
          this.keywordOverlays[index] = [];
        }
        // Remove the map
        delete this.keywordMaps[index];
      }
      
      const keyword = this.keywordDetailedData && this.keywordDetailedData[index] 
        ? this.keywordDetailedData[index] 
        : this.dynamicKeywordData[index];
        
      if (!keyword) {
        return;
      }
      
      // Get center from location data or first item
      let center = { lat: 28.5887354, lng: 77.0829215 }; // Default center
      
      if (keyword.locationData && keyword.locationData.length > 0) {
        const firstLocation = keyword.locationData[0];
        center = {
          lat: firstLocation.lat,
          lng: firstLocation.lng
        };
      } else if (keyword.items && keyword.items.length > 0) {
        const firstItem = keyword.items[0];
        center = {
          lat: parseFloat(firstItem.latitude),
          lng: parseFloat(firstItem.longitude)
        };
      }
      
      // Initialize maps storage if needed
      if (!this.keywordMaps) {
        this.keywordMaps = {};
      }
      
      // Create new map instance
      this.keywordMaps[index] = new window.google.maps.Map(mapElement[0], {
        zoom: 13,
        center: center,
        mapTypeId: 'roadmap',
        disableDefaultUI: true,
        gestureHandling: 'cooperative',
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          }
        ]
      });
      
      // Initial heatmap update
      this.updateKeywordHeatmap(index, this.keywordSelectedPeriods[index] || 30);
    },
    
    oldUpdateKeywordHeatmap(index, period) {
      // Re-initialize heatmap with new data based on period
      if (this.keywordMaps[index]) {
        this.initKeywordHeatmap(index);
      }
    },
    
    zoomInKeywordMap(index) {
      // Implementation for zooming in on keyword map
      const map = this.keywordMaps[index];
      if (map) {
        map.setZoom(map.getZoom() + 1);
      }
    },
    
    zoomOutKeywordMap(index) {
      // Implementation for zooming out on keyword map
      const map = this.keywordMaps[index];
      if (map) {
        map.setZoom(map.getZoom() - 1);
      }
    },
    
    resetKeywordMapView(index) {
      // Implementation for resetting keyword map view
      const map = this.keywordMaps[index];
      if (map && this.keywordDetailedData[index]) {
        const locationData = this.keywordDetailedData[index].locationData || [];
        if (locationData.length > 0) {
          const bounds = new google.maps.LatLngBounds();
          locationData.forEach(location => {
            bounds.extend(new google.maps.LatLng(location.lat, location.lng));
          });
          map.fitBounds(bounds);
        }
      }
    }
  }
}
</script>

<style scoped>
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>

<style scoped src="./GridScanDetails.css"></style>